import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { auth, db, storage } from '../firebase';
import { 
  collection, addDoc, query, orderBy, onSnapshot, serverTimestamp, 
  doc, getDoc, updateDoc, arrayUnion, arrayRemove, deleteDoc, increment, where, Timestamp, limit, setDoc, startAfter, getDocs
} from 'firebase/firestore';
import {
  getDownloadURL,
  ref as storageRef,
  uploadBytesResumable
} from 'firebase/storage';

// ===============================================
// Helpers & Handlers
// ===============================================
function getLocalContacts() {
  try { return JSON.parse(localStorage.getItem('nexora_local_contacts') || '{}'); } catch (e) { return {}; }
}
function getCachedData(key) {
  try { return JSON.parse(localStorage.getItem(key)) || []; } catch(e) { return []; }
}
function getCachedObj(key) {
  try { return JSON.parse(localStorage.getItem(key)) || {}; } catch(e) { return {}; }
}

function getGroupCache(groupId) {
  try {
    if (!groupId) return { messages: [], unreadIds: [], unreadCount: 0, savedAt: 0 };
    const raw = localStorage.getItem(`nexora_group_msgs_${groupId}`);
    if (!raw) return { messages: [], unreadIds: [], unreadCount: 0, savedAt: 0 };
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return { messages: parsed, unreadIds: [], unreadCount: 0, savedAt: 0 };
    }
    const unreadIds = Array.isArray(parsed.unreadIds) ? parsed.unreadIds : [];
    return {
      messages: Array.isArray(parsed.messages) ? parsed.messages : [],
      unreadIds,
      firstUnreadId: parsed.firstUnreadId || unreadIds[0] || null,
      unreadCount: Number(parsed.unreadCount) || 0,
      savedAt: Number(parsed.savedAt) || 0
    };
  } catch (e) {
    return { messages: [], unreadIds: [], unreadCount: 0, savedAt: 0 };
  }
}

function saveGroupCache(groupId, messages, unreadIds = [], unreadCount = unreadIds.length) {
  try {
    if (!groupId || !Array.isArray(messages)) return;
    const cachedMessages = messages.length > 50 ? messages.slice(messages.length - 50) : messages;
    localStorage.setItem(
      `nexora_group_msgs_${groupId}`,
      JSON.stringify({
        messages: cachedMessages,
        unreadIds: Array.isArray(unreadIds) ? unreadIds.slice(-50) : [],
        firstUnreadId: Array.isArray(unreadIds) && unreadIds.length ? unreadIds[0] : null,
        unreadCount: Number(unreadCount) || 0,
        savedAt: Date.now()
      })
    );
  } catch (e) {
    console.warn('Group cache save skipped:', e);
  }
}

const IMGBB_API_KEY = "af3aa5305ed243a9489b195b87a2afe5"; 

const uploadToImgBB = async (file) => {
  const formData = new FormData();
  formData.append('image', file);
  try {
    const res = await fetch(`https://api.imgbb.com/1/upload?key=${IMGBB_API_KEY}`, { method: 'POST', body: formData });
    const data = await res.json();
    return data.data?.url || null;
  } catch (e) { return null; }
};

const compressImage = (file, isSticker = false) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width; let height = img.height; 
        const maxWidth = isSticker ? 512 : 1000; 
        if (width > maxWidth) { height = Math.round((height * maxWidth) / width); width = maxWidth; }
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL(isSticker ? 'image/png' : 'image/jpeg', 0.8));
      };
    };
  });
};

const buildRealWaveform = async (audioBlob) => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const arrayBuffer = await audioBlob.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    const rawData = audioBuffer.getChannelData(0);
    const samples = 25;
    const blockSize = Math.floor(rawData.length / samples);
    const filteredData = [];
    for (let i = 0; i < samples; i++) {
      let blockStart = blockSize * i;
      let sum = 0;
      for (let j = 0; j < blockSize; j++) { sum = sum + Math.abs(rawData[blockStart + j]); }
      filteredData.push(Math.max(6, Math.min(36, (sum / blockSize) * 200)));
    }
    return filteredData;
  } catch (e) {
    return Array.from({ length: 25 }, () => Math.floor(Math.random() * 15) + 10);
  }
};

const makeFallbackWaveform = (id) => {
  const seed = (id || "default").split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return Array.from({ length: 25 }, (_, i) => Math.floor(Math.abs(Math.sin(seed + i) * 20) + 10));
};

const GroupScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { groupId: paramGroupId } = useParams();
  
  const actualGroupId = paramGroupId || location.state?.groupId;

  const [creationPhase, setCreationPhase] = useState(actualGroupId ? 'chat_screen' : 'select_members');
  const [allUsersList, setAllUsersList] = useState([]);
  const [userProfilesMap, setUserProfilesMap] = useState(getCachedObj('nexora_dash_users_map'));
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupPhotoURL, setNewGroupPhotoURL] = useState('');
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);

  // Chat State
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const textareaRef = useRef(null);
  const [currentUid, setCurrentUid] = useState(null);
  const [currentUserData, setCurrentUserData] = useState(null);
  
  // Instant pre-hydration for Group Data
  const [groupData, setGroupData] = useState(() => {
    if (location.state?.preloadedGroupData) {
      return location.state.preloadedGroupData;
    }
    return getCachedObj(`nexora_group_data_${actualGroupId}`) || null;
  });

  // Keep the complete preloaded cache so unreadIds/unreadCount can position
  // the current user's first unread message before Firestore finishes loading.
  const preloadedGroupCacheRef = useRef(null);

  // Instant pre-hydration for Messages from location.state or Cache (0ms Delay)
  const [messages, setMessages] = useState(() => {
    try {
      const preloadedCache = location.state?.preloadedGroupCache;
      const cached = preloadedCache || getGroupCache(actualGroupId);
      preloadedGroupCacheRef.current = cached;

      if (cached?.messages && cached.messages.length > 0) return cached.messages;
      if (location.state?.preloadedMessages && location.state.preloadedMessages.length > 0) {
        return location.state.preloadedMessages;
      }
      return [];
    } catch (e) {
      return [];
    }
  });

  const [text, setText] = useState('');
  const [highlightedMsgId, setHighlightedMsgId] = useState(null); 

  // Custom Toast State
  const [toastMsg, setToastMsg] = useState(null);
  const toastTimerRef = useRef(null);

  const showToast = (msg) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToastMsg(msg);
    toastTimerRef.current = setTimeout(() => setToastMsg(null), 2000);
  };

  // Unread & Auto-scroll State
  const firstUnreadIdRef = useRef(null);
  const totalUnreadCountRef = useRef(0);
  const [showScrollBottom, setShowScrollBottom] = useState(false);
  const [initialPositionReady, setInitialPositionReady] = useState(false);
  const autoScrollLockedRef = useRef(false);

  // Pagination & loading for group messages
  const [hasMoreMsgs, setHasMoreMsgs] = useState(true);
  const [isFetchingOld, setIsFetchingOld] = useState(false);
  const [lastVisibleMsg, setLastVisibleMsg] = useState(null);

  // Unread landing/banner state
  const [groupUnreadCount, setGroupUnreadCount] = useState(0);
  const [showUnreadBanner, setShowUnreadBanner] = useState(false);
  const [unreadBannerCount, setUnreadBannerCount] = useState(0);
  const unreadBannerTimerRef = useRef(null);
  const unreadMarkedSeenRef = useRef(false);

  // Media & Upload
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  // Voice Recorder
  const [showVoiceModal, setShowVoiceModal] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [waveBars, setWaveBars] = useState([12, 22, 35, 18, 30, 10, 28, 40, 15, 18]);
  const [playingVoiceId, setPlayingVoiceId] = useState(null);
  const [voicePlayback, setVoicePlayback] = useState({});

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingIntervalRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animFrameRef = useRef(null);

  // Mentions, Pinned, Stickers & Modals
  const [showMentionList, setShowMentionList] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [groupMembersList, setGroupMembersList] = useState(getCachedData(`nexora_group_members_${actualGroupId}`));
  const [unreadMentions, setUnreadMentions] = useState([]);
  const [showMentionModal, setShowMentionModal] = useState(false);
  const [activePinIndex, setActivePinIndex] = useState(0); 
  
  // Custom Sticker State
  const defaultStickers = [
    "https://cdn-icons-png.flaticon.com/512/4860/4860953.png",
    "https://cdn-icons-png.flaticon.com/512/4860/4860927.png",
    "https://cdn-icons-png.flaticon.com/512/10543/10543085.png"
  ];
  const [savedStickers, setSavedStickers] = useState(getCachedData('nexora_saved_stickers').length ? getCachedData('nexora_saved_stickers') : defaultStickers);
  const [showStickerModal, setShowStickerModal] = useState(false);
  const stickerImportRef = useRef(null);
  const stickerLongPressTimerRef = useRef(null);

  // Magic Meet Card State
  const [showMeetForm, setShowMeetForm] = useState(false);
  const [meetCompanyName, setMeetCompanyName] = useState('Selfgars');
  const [meetTime, setMeetTime] = useState('২:৩০');
  const [meetLinkInput, setMeetLinkInput] = useState('');

  const [showGroupInfoModal, setShowContactModal] = useState(false);
  const [showAddMemberScreen, setShowAddMemberScreen] = useState(false);
  const [membersToAdd, setMembersToAdd] = useState([]);
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const [connectionMembersLoading, setConnectionMembersLoading] = useState(false);
  const [connectionMembers, setConnectionMembers] = useState([]);
  const [isEditingInfo, setIsEditingInfo] = useState(false);
  const [editGroupName, setEditGroupName] = useState('');
  const [editGroupBio, setEditGroupBio] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);

  // Modals, Actions & Multi-Select
  const [fullScreenMedia, setFullScreenMedia] = useState(null);
  const [replyingToMsg, setReplyingToMsg] = useState(null);
  const [editingMsg, setEditingMsg] = useState(null);
  const [editInputText, setEditInputText] = useState('');
  const [messageActionModal, setMessageActionModal] = useState(null);
  const [msgDetails, setMsgDetails] = useState(null);
  const [reactionPopup, setReactionPopup] = useState(null);
  const [customEmojiMsgId, setCustomEmojiMsgId] = useState(null);
  const [swipeX, setSwipeX] = useState({});
  const [selectedMessageIds, setSelectedMessageIds] = useState([]); 

  // Forward Modal State
  const [showForwardModal, setShowForwardModal] = useState(false);
  const [forwardTargets, setForwardTargets] = useState([]);
  const [selectedForwardIds, setSelectedForwardIds] = useState([]);
  const [msgToForward, setMsgToForward] = useState(null); 

  const isInitialLoadRef = useRef(true);
  const isFirstSnapshotProcessing = useRef(true);
  const msgLongPressTimerRef = useRef(null);
  const clearedUnreadRef = useRef(false);
  const autoClearTimerRef = useRef(null);
  const pendingMessagesRef = useRef(new Map());
  const shouldScrollToOwnMessageRef = useRef(false);
  const initialLandingDoneRef = useRef(false);
  const initialSnapshotReadyRef = useRef(false);
  const suppressInitialTopLoadRef = useRef(false);
  
  const clickTimers = useRef({});
  const clickCounts = useRef({});
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);

  const logoInputRef = useRef(null);
  const newGroupLogoRef = useRef(null);
  const mediaInputRef = useRef(null); 
  const fileInputRef = useRef(null);  
  const localContacts = getLocalContacts();

  const normalizePhone = (p) => {
    if (!p) return '';
    let d = String(p).replace(/\D/g, '');
    if (!d) return '';
    if (d.startsWith('880')) d = d.substring(3);
    if (d.startsWith('0')) d = d.substring(1);
    return `+880${d}`;
  };

  const getCurrentUserConnectionMembers = async (excludeUids = []) => {
    if (!currentUid) return [];
    const myPhone = normalizePhone(currentUid);
    if (!myPhone) return [];

    try {
      setConnectionMembersLoading(true);
      const connectionSnap = await getDoc(doc(db, 'connections', myPhone));
      const rawContacts = connectionSnap.exists() ? (connectionSnap.data()?.contacts || {}) : {};
      const profilesByPhone = new Map();
      allUsersList.forEach((user) => {
        const normalized = normalizePhone(user?.phoneNumber);
        if (normalized && user?.uid) profilesByPhone.set(normalized, user);
      });

      const memberList = Object.entries(rawContacts)
        .map(([phoneKey, value]) => {
          const candidatePhone = normalizePhone(typeof value === 'object' ? (value?.phoneNumber || phoneKey) : phoneKey);
          const matchedUser = profilesByPhone.get(candidatePhone || normalizePhone(phoneKey));
          if (!matchedUser) return null;

          const savedContactName = localContacts[candidatePhone || normalizePhone(phoneKey)] || (typeof value === 'object' ? value?.name : value) || matchedUser.fullName || candidatePhone || normalizePhone(phoneKey);

          return {
            uid: matchedUser.uid,
            phoneNumber: matchedUser.phoneNumber,
            displayName: savedContactName,
            photoURL: matchedUser.photoURL || ''
          };
        })
        .filter(Boolean);

      const uniqueMap = new Map();
      memberList.forEach((user) => {
        const key = user.uid || user.phoneNumber;
        if (!excludeUids.includes(key) && key !== currentUid && !uniqueMap.has(key)) {
          uniqueMap.set(key, user);
        }
      });

      const ordered = Array.from(uniqueMap.values()).sort((a, b) => (a.displayName || '').localeCompare(b.displayName || '', 'bn', { sensitivity: 'base' }));
      setConnectionMembers(ordered);
      return ordered;
    } catch (e) {
      setConnectionMembers([]);
      return [];
    } finally {
      setConnectionMembersLoading(false);
    }
  };

  const handleNavigate = (path, state = {}) => {
    navigate(path, state);
  };

  useEffect(() => {
    const handleContextMenu = (e) => {
      if (e.target.closest('input, textarea, [contenteditable="true"]')) return;
      e.preventDefault();
    };
    document.addEventListener('contextmenu', handleContextMenu);
    return () => document.removeEventListener('contextmenu', handleContextMenu);
  }, []);

  // User Fetch
  useEffect(() => {
    const userUid = localStorage.getItem('nexora_user_uid') || auth.currentUser?.uid;
    if (!userUid) { navigate('/login'); return; }
    setCurrentUid(userUid);

    getDoc(doc(db, 'users', userUid)).then(snap => {
      if (snap.exists()) setCurrentUserData(snap.data());
    });

    const unsubUsers = onSnapshot(collection(db, 'users'), (snapshot) => {
      const uList = [];
      const pMap = {};
      snapshot.forEach(docSnap => {
        const data = docSnap.data();
        if (data.uid !== userUid) uList.push(data);
        pMap[data.uid] = data;
      });
      setAllUsersList(uList);
      setUserProfilesMap(pMap);
    });

    const savedChats = getCachedData('nexora_dash_chats');
    const savedGroups = getCachedData('nexora_dash_groups');
    const savedComms = getCachedData('nexora_dash_comms');
    setForwardTargets([...savedChats, ...savedGroups, ...savedComms]);

    return () => unsubUsers();
  }, [navigate]);

  // Group & Messages Fetch + Clear Unread instantly
  useEffect(() => {
    if (creationPhase !== 'chat_screen' || !actualGroupId || !currentUid) return;

    // Do not clear unread state before the cache-first landing position is
    // established. markUnreadAsSeen() clears only this user's counter after
    // the first unread divider has been positioned.
    clearedUnreadRef.current = false;
    unreadMarkedSeenRef.current = false;

    if (autoClearTimerRef.current) clearTimeout(autoClearTimerRef.current);

    const unsubGroup = onSnapshot(doc(db, 'groups', actualGroupId), async (groupSnap) => {
      if (groupSnap.exists()) {
        const gData = { id: groupSnap.id, ...groupSnap.data() };
        setGroupData(gData);
        setGroupUnreadCount(0); // গ্রুপের আনরিড অলওয়েজ ০

        try {
          localStorage.setItem(`nexora_group_data_${actualGroupId}`, JSON.stringify({
            ...gData,
            unreadCount: { ...(gData.unreadCount || {}), [currentUid]: 0 }
          }));
        } catch (e) {}
        
        setEditGroupName(gData.name || '');
        setEditGroupBio(gData.bio || '');

        if (gData.members && gData.members.length > 0) {
          const mList = [];
          for (let mUid of gData.members) {
            try {
              const uSnap = await getDoc(doc(db, 'users', mUid));
              if (uSnap.exists()) {
                const uData = uSnap.data();
                const normP = normalizePhone(uData.phoneNumber);
                const savedN = localContacts[normP];
                const uRole = uData.role?.toLowerCase();
                const isSpecialUser = uRole === 'admin' || uRole === 'premium' || uRole === 'owner';
                const displayN = isSpecialUser ? uData.fullName : (savedN || normP || uData.fullName || 'Member');
                mList.push({ uid: mUid, displayName: displayN, photoURL: uData.photoURL, role: uData.role });
              }
            } catch (e) {}
          }
          setGroupMembersList(mList);
          try {
            localStorage.setItem(`nexora_group_members_${actualGroupId}`, JSON.stringify(mList));
          } catch(e) {}
        }
      }
    });

    const MSG_PAGE_SIZE = 50;
    const q = query(collection(db, 'groups', actualGroupId, 'messages'), orderBy('createdAt', 'desc'), limit(MSG_PAGE_SIZE));
    
    isInitialLoadRef.current = true;
    isFirstSnapshotProcessing.current = true;
    initialLandingDoneRef.current = false;
    initialSnapshotReadyRef.current = false;
    shouldScrollToOwnMessageRef.current = false;

    const unsubMessages = onSnapshot(q, (snapshot) => {
      if (snapshot.empty) return;

      const fetchedDesc = [];
      let firstId = null;
      let unreadCount = 0;

      snapshot.forEach(docSnap => {
        const msgData = docSnap.data();
        const msg = { id: docSnap.id, ...msgData };
        fetchedDesc.push(msg);
        const isUnread = msg.type !== 'system' && msg.senderId !== currentUid && (!msg.seenBy || !msg.seenBy[currentUid]);
        if (isUnread) {
          if (!firstId) firstId = msg.id;
          unreadCount++;
        }
      });

      const fetched = fetchedDesc.slice().reverse();

      setLastVisibleMsg(snapshot.docs[snapshot.docs.length - 1] || null);
      if (snapshot.size < MSG_PAGE_SIZE) setHasMoreMsgs(false); else setHasMoreMsgs(true);

      if (isFirstSnapshotProcessing.current) {
        const cachedMeta = preloadedGroupCacheRef.current || getGroupCache(actualGroupId);
        const cachedIds = Array.isArray(cachedMeta?.unreadIds) ? cachedMeta.unreadIds : [];
        const cachedCount = Number(cachedMeta?.unreadCount) || 0;
        const validCachedIds = cachedIds.filter(id => fetchedDesc.some(m => m.id === id));

        if (validCachedIds.length > 0) {
          firstUnreadIdRef.current = validCachedIds[0];
          totalUnreadCountRef.current = cachedCount || validCachedIds.length;
        } else {
          // Firestore query is DESC, so choose the oldest unread from the
          // chronological list instead of the newest unread.
          const unreadChronological = fetched
            .filter(msg => msg.type !== 'system' && msg.senderId !== currentUid &&
              (!msg.seenBy || !msg.seenBy[currentUid]))
            .sort((a, b) => {
              const ta = a.createdAt?.toMillis ? a.createdAt.toMillis() : (a.createdAt?.seconds ? a.createdAt.seconds * 1000 : (typeof a.createdAt === 'number' ? a.createdAt : 0));
              const tb = b.createdAt?.toMillis ? b.createdAt.toMillis() : (b.createdAt?.seconds ? b.createdAt.seconds * 1000 : (typeof b.createdAt === 'number' ? b.createdAt : 0));
              return ta - tb;
            });
          firstUnreadIdRef.current = unreadChronological[0]?.id || null;
          totalUnreadCountRef.current = unreadChronological.length;
        }

        initialSnapshotReadyRef.current = true;
        isFirstSnapshotProcessing.current = false;
      }

      const pending = Array.from(pendingMessagesRef.current.values());
      pending.forEach(pm => {
        if (fetched.some(m => m.clientMessageId && m.clientMessageId === pm.clientMessageId)) {
          pendingMessagesRef.current.delete(pm.clientMessageId);
        }
      });
      const stillPending = Array.from(pendingMessagesRef.current.values());

      const merged = [...fetched, ...stillPending].sort((a, b) => {
        const ta = a.createdAt?.toMillis ? a.createdAt.toMillis() : (a.createdAt?.seconds ? a.createdAt.seconds * 1000 : (typeof a.createdAt === 'number' ? a.createdAt : 0));
        const tb = b.createdAt?.toMillis ? b.createdAt.toMillis() : (b.createdAt?.seconds ? b.createdAt.seconds * 1000 : (typeof b.createdAt === 'number' ? b.createdAt : 0));
        return ta - tb;
      });

      setMessages(merged);

      try {
        const previousCache = preloadedGroupCacheRef.current || getGroupCache(actualGroupId);
        const keepIds = Array.isArray(previousCache?.unreadIds)
          ? previousCache.unreadIds.filter(id => merged.some(m => m.id === id))
          : [];
        const keepCount = Number(previousCache?.unreadCount) || totalUnreadCountRef.current || 0;
        saveGroupCache(
          actualGroupId,
          merged,
          keepIds.length ? keepIds : (firstUnreadIdRef.current ? [firstUnreadIdRef.current] : []),
          keepCount
        );
      } catch (e) {}
    });

    return () => {
      unsubGroup(); 
      unsubMessages();
      if (autoClearTimerRef.current) { clearTimeout(autoClearTimerRef.current); autoClearTimerRef.current = null; }
    };
  }, [actualGroupId, creationPhase, currentUid]);

  // Mention notifications check
  useEffect(() => {
    if (!currentUserData || messages.length === 0) return;
    const myName = currentUserData.fullName || '';
    const myPhone = normalizePhone(currentUserData.phoneNumber);
    const mySavedName = localContacts[myPhone] || myName;

    if (isFirstSnapshotProcessing.current) return;

    const unreadMentionsNow = messages.filter(msg => {
      if (msg.senderId === currentUid || msg.type !== 'text') return false;
      const unread = !msg.seenBy || !msg.seenBy[currentUid];
      return unread && (msg.text?.includes('@All Mention') || msg.text?.includes(`@${mySavedName}`) || msg.text?.includes(`@${myName}`));
    });
    setUnreadMentions(unreadMentionsNow);
  }, [messages, currentUserData, currentUid]);

  const markUnreadAsSeen = async () => {
    if (!actualGroupId || !currentUid || unreadMarkedSeenRef.current) return;
    unreadMarkedSeenRef.current = true;

    try {
      const cachedUnreadIds = Array.isArray(preloadedGroupCacheRef.current?.unreadIds)
        ? preloadedGroupCacheRef.current.unreadIds : [];

      const unreadIds = (cachedUnreadIds.length ? cachedUnreadIds : messages
        .filter(msg => msg.type !== 'system' && msg.senderId !== currentUid &&
          (!msg.seenBy || !msg.seenBy[currentUid]))
        .map(msg => msg.id)).filter(Boolean);

      // Only the currently logged-in user's seenBy field is changed.
      await Promise.all(unreadIds.map(id =>
        updateDoc(doc(db, 'groups', actualGroupId, 'messages', id), {
          [`seenBy.${currentUid}`]: { time: Date.now(), listened: false }
        })
      ));

      // Only this user's group counter is cleared. Other members' counters
      // remain untouched.
      await updateDoc(doc(db, 'groups', actualGroupId), {
        [`unreadCount.${currentUid}`]: 0
      });

      setGroupUnreadCount(0);
      setGroupData(prev => prev
        ? {
            ...prev,
            unreadCount: {
              ...(prev.unreadCount || {}),
              [currentUid]: 0
            }
          }
        : prev
      );

      try {
        window.dispatchEvent(new CustomEvent('nexoraGroupSeenSync', {
          detail: { groupId: actualGroupId, unreadCount: 0 }
        }));
      } catch (e) {}

      setMessages(prev => prev.map(msg => {
        if (msg.senderId === currentUid || msg.type === 'system') return msg;
        if (msg.seenBy?.[currentUid]) return msg;
        return {
          ...msg,
          seenBy: {
            ...(msg.seenBy || {}),
            [currentUid]: { time: Date.now(), listened: false }
          }
        };
      }));

      try {
        const updated = (messages || []).map(msg => {
          if (msg.senderId === currentUid || msg.type === 'system') return msg;
          if (msg.seenBy?.[currentUid]) return msg;
          return {
            ...msg,
            seenBy: {
              ...(msg.seenBy || {}),
              [currentUid]: { time: Date.now(), listened: false }
            }
          };
        });
        saveGroupCache(actualGroupId, updated, [], 0);
        preloadedGroupCacheRef.current = {
          ...(preloadedGroupCacheRef.current || {}),
          messages: updated,
          unreadIds: [],
          firstUnreadId: null,
          unreadCount: 0,
          savedAt: Date.now()
        };
      } catch (e) {}
    } catch (e) {
      unreadMarkedSeenRef.current = false;
    }
  };

  // Initial viewport positioning:
  // Dashboard already calculated the first unread anchor. GroupScreen only
  // applies that cached anchor before the first paint; it does not animate or
  // scroll to the position afterwards.
  useLayoutEffect(() => {
    if (creationPhase !== 'chat_screen' || !chatContainerRef.current) return;

    const container = chatContainerRef.current;

    if (isInitialLoadRef.current) {
      const cached = preloadedGroupCacheRef.current || getGroupCache(actualGroupId);

      // Prefer Dashboard's exact anchor. Fall back to the first cached unread
      // id only for compatibility with older cache entries.
      if (!firstUnreadIdRef.current) {
        const cachedFirstUnreadId =
          cached?.firstUnreadId ||
          (Array.isArray(cached?.unreadIds) ? cached.unreadIds[0] : null);

        if (cachedFirstUnreadId && messages.some(m => m.id === cachedFirstUnreadId)) {
          firstUnreadIdRef.current = cachedFirstUnreadId;
          totalUnreadCountRef.current =
            Number(cached?.unreadCount) ||
            (Array.isArray(cached?.unreadIds) ? cached.unreadIds.length : 0);
        }
      }

      const unreadEl = firstUnreadIdRef.current
        ? document.getElementById(`unread-divider-${firstUnreadIdRef.current}`)
        : null;

      if (firstUnreadIdRef.current && unreadEl) {
        // The chat container starts immediately below the Group header.
        // Therefore its top edge is the maximum upward boundary: the unread
        // divider can never be positioned above the header.
        const targetTop = Math.max(0, unreadEl.offsetTop);

        // Force non-animated positioning even if global CSS sets
        // scroll-behavior:smooth.
        const previousBehavior = container.style.scrollBehavior;
        container.style.scrollBehavior = 'auto';
        container.scrollTop = targetTop;
        container.style.scrollBehavior = previousBehavior;

        autoScrollLockedRef.current = true;
        setShowScrollBottom(
          container.scrollHeight - container.scrollTop - container.clientHeight > 150
        );
        isInitialLoadRef.current = false;

        // Only mark this user's unread messages after the initial viewport has
        // been positioned. Other group members' unread counters are untouched.
        setTimeout(() => markUnreadAsSeen(), 100);

        // React flushes this state update before the browser paints, so the
        // user never sees the pre-positioned/default viewport.
        setInitialPositionReady(true);
      } else if (!firstUnreadIdRef.current) {
        // No unread messages: open at the latest message once the initial
        // Firestore snapshot is ready, while still avoiding an animated jump.
        if (initialSnapshotReadyRef.current || messages.length > 0) {
          const previousBehavior = container.style.scrollBehavior;
          container.style.scrollBehavior = 'auto';
          container.scrollTop = container.scrollHeight;
          container.style.scrollBehavior = previousBehavior;

          autoScrollLockedRef.current = false;
          setShowScrollBottom(false);
          isInitialLoadRef.current = false;
          setInitialPositionReady(true);
        }
      }
    } else if (
      messagesEndRef.current &&
      !showScrollBottom &&
      !autoScrollLockedRef.current &&
      !isFetchingOld
    ) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, creationPhase, actualGroupId]);

  useEffect(() => {
    return () => {
      if (unreadBannerTimerRef.current) clearTimeout(unreadBannerTimerRef.current);
    };
  }, []);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const isNotAtBottom = scrollHeight - scrollTop - clientHeight > 150;
    setShowScrollBottom(isNotAtBottom);
    if (!isNotAtBottom) {
      autoScrollLockedRef.current = false; 
    }
    if (scrollTop === 0 && hasMoreMsgs && !isFetchingOld) {
      loadMoreMessages();
    }
  };

  const loadMoreMessages = async () => {
    if (!hasMoreMsgs || isFetchingOld || !lastVisibleMsg || !actualGroupId) return;
    setIsFetchingOld(true);
    try {
      const MSG_PAGE_SIZE = 50;
      const messagesRef = collection(db, 'groups', actualGroupId, 'messages');
      const q = query(messagesRef, orderBy('createdAt', 'desc'), startAfter(lastVisibleMsg), limit(MSG_PAGE_SIZE));
      const querySnapshot = await getDocs(q);
      if (querySnapshot.empty) {
        setHasMoreMsgs(false);
        setIsFetchingOld(false);
        return;
      }

      const fetchedDesc = [];
      querySnapshot.forEach(docSnap => {
        fetchedDesc.push({ id: docSnap.id, ...docSnap.data() });
      });

      const fetched = fetchedDesc.slice().reverse();
      const prevScrollHeight = chatContainerRef.current ? chatContainerRef.current.scrollHeight : 0;

      setMessages(prev => {
        const merged = [...fetched, ...prev];
        saveGroupCache(actualGroupId, merged, [], 0);
        return merged;
      });

      setLastVisibleMsg(querySnapshot.docs[querySnapshot.docs.length - 1]);
      if (querySnapshot.size < MSG_PAGE_SIZE) setHasMoreMsgs(false);

      setTimeout(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight - prevScrollHeight;
        }
      }, 60);
    } catch (e) {
      console.error('loadMoreMessages failed', e);
    }
    setIsFetchingOld(false);
  };

  const phonebookKeys = Object.keys(localContacts);
  const nexoraSavedContacts = connectionMembers.filter((u) => u.uid !== currentUid && !selectedContacts.includes(u.uid));

  useEffect(() => {
    if (!currentUid) return;
    let ignore = false;
    const run = async () => {
      const result = await getCurrentUserConnectionMembers();
      if (!ignore) setConnectionMembers(result);
    };
    run();
    return () => { ignore = true; };
  }, [currentUid, allUsersList.length]);

  const filteredAddableMembers = nexoraSavedContacts.filter((u) => {
    const q = memberSearchQuery.trim().toLowerCase();
    if (!q) return true;
    return (u.displayName || '').toLowerCase().includes(q) || (u.phoneNumber || '').toLowerCase().includes(q);
  });
  
  const isGlobalOwner = currentUserData?.role?.toLowerCase() === 'owner';
  const isAdmin = groupData?.admins?.includes(currentUid) || groupData?.adminUid === currentUid || isGlobalOwner;
  const canSend = !groupData?.onlyAdminsMessage || isAdmin;

  // Swipe & Actions
  const handleTouchStartReact = (e, msg) => {
    if(selectedMessageIds.length > 0) return; 
    const touch = e.touches[0];
    touchStartX.current = touch.clientX;
    touchStartY.current = touch.clientY;
    const posY = touch.clientY; 
    
    msgLongPressTimerRef.current = setTimeout(() => {
      if (navigator.vibrate) navigator.vibrate(50);
      setReactionPopup({ msg, y: posY });
    }, 500);
  };

  const handleTouchMoveReact = (e, msg) => {
    if(selectedMessageIds.length > 0) return;
    const touch = e.touches[0];
    const diffX = touch.clientX - touchStartX.current;
    const diffY = Math.abs(touch.clientY - touchStartY.current);

    if (diffY > 20) {
      if (msgLongPressTimerRef.current) clearTimeout(msgLongPressTimerRef.current);
      setSwipeX({}); return;
    }

    if (Math.abs(diffX) > 10) {
      if (msgLongPressTimerRef.current) clearTimeout(msgLongPressTimerRef.current);
      let limitedX = diffX;
      if (limitedX > 50) limitedX = 50;
      if (limitedX < -50) limitedX = -50;
      setSwipeX({ [msg.id]: limitedX });
    }
  };

  const handleTouchEndReact = (e, msg) => {
    if (msgLongPressTimerRef.current) clearTimeout(msgLongPressTimerRef.current);
    if(selectedMessageIds.length > 0) return;
    
    const diffX = swipeX[msg.id] || 0;
    if (Math.abs(diffX) >= 35) {
      if (navigator.vibrate) navigator.vibrate(50);
      setReplyingToMsg(msg); setEditingMsg(null);
    }
    setSwipeX({}); 
  };

  const toggleSelection = (msgId) => {
    if(selectedMessageIds.includes(msgId)) {
        const newArr = selectedMessageIds.filter(id => id !== msgId);
        setSelectedMessageIds(newArr);
    } else {
        setSelectedMessageIds([...selectedMessageIds, msgId]);
    }
  };

  const handleMsgClick = (e, msg) => {
    e.stopPropagation();
    const id = msg.id;

    if (selectedMessageIds.length > 0) {
        toggleSelection(id);
        return;
    }

    clickCounts.current[id] = (clickCounts.current[id] || 0) + 1;
    if (clickCounts.current[id] === 3) {
      if (navigator.vibrate) navigator.vibrate(50);
      setMessageActionModal(msg);
      clickCounts.current[id] = 0;
    }
    clearTimeout(clickTimers.current[id]);
    clickTimers.current[id] = setTimeout(() => { clickCounts.current[id] = 0; }, 400);
  };

  const handleReactToPost = async (msgId, emoji) => {
    if (!actualGroupId || !currentUid) return;
    try {
      const msgRef = doc(db, 'groups', actualGroupId, 'messages', msgId);
      await setDoc(msgRef, {
        reactions: {
          [currentUid]: emoji
        }
      }, { merge: true });

      setMessages(prev => {
        const updated = prev.map(m => {
          if (m.id === msgId) {
            const currentReactions = m.reactions || {};
            return {
              ...m,
              reactions: { ...currentReactions, [currentUid]: emoji }
            };
          }
          return m;
        });
        return updated;
      });

      setReactionPopup(null);
    } catch (e) {
      console.error("Reaction failed:", e);
    }
  };

  const handlePhoneClick = async (phoneStr) => {
    const normP = normalizePhone(phoneStr);
    const matchedUser = allUsersList.find(u => normalizePhone(u.phoneNumber) === normP);
    if (matchedUser) {
      handleNavigate('/chat/' + matchedUser.uid);
    } else {
      if (await window.nexoraConfirm("এই নম্বরটি নেক্সোরাতে নেই। আপনি কি ডায়ালারে কল করতে চান?")) {
        window.location.href = `tel:${phoneStr}`;
      }
    }
  };

  const handleLinkTouchStart = (e, textStr) => {
    e.stopPropagation();
    clickTimers.current['link'] = setTimeout(() => {
      if (navigator.vibrate) navigator.vibrate(50);
      navigator.clipboard.writeText(textStr);
      showToast("Copied!");
    }, 500);
  };

  const handleLinkTouchEnd = (e) => {
    e.stopPropagation();
    clearTimeout(clickTimers.current['link']);
  };

  const renderTextWithLinks = (textStr) => {
    if (!textStr) return '';
    return textStr.split('\n').map((line, lineIndex) => {
      const complexRegex = /(https?:\/\/[^\s]+|(?:www\.)?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(?:\/[^\s]*)?|(?:\+880|880|0)1[3-9]\d{8}|\b\d+(?:[.,]\d+)*\b|@[a-zA-Z0-9_.-]+|@All Mention|@\w+\s\w+|\*\*.*?\*\*|\*[^*]+\*)/g;
      const parts = line.split(complexRegex);
      
      return (
        <React.Fragment key={lineIndex}>
          {parts.map((part, i) => {
            if (!part) return null;
            if (part.match(/^(?:\+880|880|0)1[3-9]\d{8}$/)) {
              return <span key={i} onTouchStart={(e) => handleLinkTouchStart(e, part)} onTouchEnd={handleLinkTouchEnd} onClick={(e) => { e.stopPropagation(); handlePhoneClick(part); }} style={{ color: '#2563eb', fontWeight: '800', textDecoration: 'underline', cursor: 'pointer', pointerEvents: 'auto' }}>{part}</span>;
            }
            else if (/^\d+(?:[.,]\d+)*$/.test(part)) {
              return (
                <span
                  key={i}
                  onTouchStart={(e) => handleLinkTouchStart(e, part)}
                  onTouchEnd={handleLinkTouchEnd}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(part);
                    showToast("Copied!");
                  }}
                  style={{
                    color: '#2563eb',
                    fontWeight: '800',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    pointerEvents: 'auto'
                  }}
                >
                  {part}
                </span>
              );
            }
            else if (part.match(/^(https?:\/\/[^\s]+|(?:www\.)?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(?:\/[^\s]*)?)$/)) {
              const href = part.startsWith('http') ? part : 'https://' + part;
              return <span key={i} onTouchStart={(e) => handleLinkTouchStart(e, part)} onTouchEnd={handleLinkTouchEnd} onClick={(e)=>{ e.stopPropagation(); window.open(href, '_blank'); }} style={{ color: '#2563eb', fontWeight: '800', textDecoration: 'underline', cursor: 'pointer', pointerEvents: 'auto' }}>{part}</span>;
            } 
            else if (part.match(/^@([a-zA-Z0-9_.-]+|All Mention|\w+\s\w+)$/)) {
              return (
                <span key={i} style={{ color: '#16a34a', fontWeight: '900', background: '#dcfce7', padding: '1px 6px', borderRadius: '6px' }}>
                  {part}
                </span>
              );
            } 
            else if (part.startsWith('**') && part.endsWith('**')) { return <strong key={i}>{part.slice(2, -2)}</strong>; } 
            else if (part.startsWith('*') && part.endsWith('*')) { return <strong key={i}>{part.slice(1, -1)}</strong>; }
            return <span key={i}>{part}</span>;
          })}
          <br />
        </React.Fragment>
      );
    });
  };

  const handleDeleteMessage = async (msgId) => {
    if (await window.nexoraConfirm("মেসেজটি ডিলেট করতে চান?")) {
      try { 
        await deleteDoc(doc(db, 'groups', actualGroupId, 'messages', msgId)); 
        setMessages(prev => prev.filter(m => m.id !== msgId));
        setFullScreenMedia(null); 
        setMessageActionModal(null); 
        showToast("Message deleted!");
      } catch(e){}
    }
  };

  const handleMultiDelete = async () => {
    if (await window.nexoraConfirm(`${selectedMessageIds.length} টি মেসেজ ডিলেট করতে চান?`)) {
        for(let id of selectedMessageIds) {
            const m = messages.find(x => x.id === id);
            if(m && (m.senderId === currentUid || isAdmin)) {
                await deleteDoc(doc(db, 'groups', actualGroupId, 'messages', id)).catch(()=>{});
            }
        }
        setMessages(prev => prev.filter(m => !selectedMessageIds.includes(m.id)));
        setSelectedMessageIds([]);
        showToast("Deleted!");
    }
  };

  const handleMultiCopy = () => {
    let combinedText = [];
    selectedMessageIds.forEach(id => {
        const m = messages.find(x => x.id === id);
        if(m && m.type === 'text' && m.text) combinedText.push(m.text);
    });
    if(combinedText.length > 0) {
        navigator.clipboard.writeText(combinedText.join('\n\n'));
        showToast("Copied!");
    }
    setSelectedMessageIds([]);
  };

  const handleMultiForward = () => {
    const msgsToFwd = selectedMessageIds.map(id => messages.find(x => x.id === id)).filter(x => x);
    setMsgToForward(msgsToFwd); 
    setShowForwardModal(true);
  };

  const getReplySummaryText = (msgObj) => {
      if (!msgObj) return null;
      if (msgObj.type === 'text') return msgObj.text;
      if (msgObj.type === 'voice') return '🎙️ Voice Message';
      if (msgObj.type === 'image') return '📷 Photo';
      if (msgObj.type === 'video') return '🎥 Video';
      if (msgObj.type === 'sticker') return '🎇 Sticker';
      if (msgObj.type === 'file') return '📄 File';
      if (msgObj.type === 'meet_card') return '📅 Meeting Card';
      if (msgObj.type === 'group_invite') return '💌 Group Invite';
      if (msgObj.type === 'community_card') return 'Community Card';
      return 'Media Message';
  };

  const handleSaveImage = (url) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = `nexora_group_media_${Date.now()}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleDeleteGroup = async () => {
    if (!actualGroupId) return;
    if (await window.nexoraConfirm("আপনি কি নিশ্চিতভাবে এই গ্রুপটি ডিলিট করতে চান? এটি আর ফিরিয়ে আনা সম্ভব নয়।")) {
      try {
        await deleteDoc(doc(db, 'groups', actualGroupId));
        navigate('/dashboard');
      } catch(e) {}
    }
  };

  const toggleContactSelection = (uid) => {
    if (selectedContacts.includes(uid)) setSelectedContacts(selectedContacts.filter(id => id !== uid));
    else setSelectedContacts([...selectedContacts, uid]);
  };

  const handleProceedToGroupInfo = () => {
    if (selectedContacts.length === 0) return;
    setCreationPhase('group_info');
  };

  const handleNewGroupLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingImage(true); const uploadedUrl = await uploadToImgBB(file); setUploadingImage(false);
    if (uploadedUrl) setNewGroupPhotoURL(uploadedUrl);
  };

  const toggleAddMemberSelection = (uid) => {
    if (membersToAdd.includes(uid)) setMembersToAdd(membersToAdd.filter(id => id !== uid));
    else setMembersToAdd([...membersToAdd, uid]);
  };

  const handleAddSelectedMembersToGroup = async () => {
    if (membersToAdd.length === 0 || !actualGroupId) return;
    setIsUploading(true);
    try {
      await updateDoc(doc(db, 'groups', actualGroupId), { members: arrayUnion(...membersToAdd) });
      for(let u of membersToAdd) {
        const uDoc = await getDoc(doc(db, 'users', u));
        if(uDoc.exists()) {
          await addDoc(collection(db, 'groups', actualGroupId, 'messages'), {
            type: 'system', text: `${uDoc.data().fullName || 'Member'} was added to the group`, createdAt: serverTimestamp(), senderId: 'system'
          });
        }
      }
      setShowAddMemberScreen(false); setMembersToAdd([]);
      showToast("Members added!");
    } catch (e) {} finally { setIsUploading(false); }
  };

  const handleCreateGroupInFirebase = async () => {
    if (!newGroupName.trim()) return;
    setIsCreatingGroup(true);
    try {
      const allMembers = [currentUid, ...selectedContacts];
      const newGroupData = {
        name: newGroupName, bio: "Hey there! We are using Nexora.", photoURL: newGroupPhotoURL, 
        members: allMembers, adminUid: currentUid, admins: [currentUid], onlyAdminsMessage: false,
        createdAt: serverTimestamp(), updatedAt: serverTimestamp(), lastMessage: "Group created by you", unreadCount: {}, pinnedMessages: []
      };
      const groupRef = await addDoc(collection(db, 'groups'), newGroupData);
      
      await addDoc(collection(db, 'groups', groupRef.id, 'messages'), {
        type: 'system', text: 'You created this group', createdAt: serverTimestamp(), senderId: 'system'
      });

      navigate('/group', { state: { groupId: groupRef.id }, replace: true });
      setCreationPhase('chat_screen');
    } catch (err) {} finally { setIsCreatingGroup(false); }
  };

  const pushMessageToFirestore = async (type, payloadExtra, summaryText) => {
    if (!currentUid || !actualGroupId) return;
    const clientMessageId = `local_${currentUid}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    const replyTxt = replyingToMsg ? getReplySummaryText(replyingToMsg) : null;
    const localMsg = {
      id: clientMessageId,
      clientMessageId,
      senderId: currentUid,
      senderName: currentUserData?.fullName || 'User',
      senderPhoto: currentUserData?.photoURL || '',
      type,
      replyToMsgId: replyingToMsg ? replyingToMsg.id : null,
      replyToMsgText: replyTxt,
      replyToSenderName: replyingToMsg ? replyingToMsg.senderName : null,
      createdAt: Date.now(),
      timeStr: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true }),
      ...payloadExtra
    };

    pendingMessagesRef.current.set(clientMessageId, localMsg);
    setMessages(prev => [...prev.filter(m => m.clientMessageId !== clientMessageId), localMsg]);

    try {
      const msgData = { ...localMsg };
      delete msgData.id;
      delete msgData.createdAt;
      msgData.createdAt = serverTimestamp();
      await addDoc(collection(db, 'groups', actualGroupId, 'messages'), msgData);

      let members = Array.isArray(groupData?.members) ? groupData.members : [];
      if ((!members || members.length === 0) && actualGroupId) {
        try {
          const gSnap = await getDoc(doc(db, 'groups', actualGroupId));
          if (gSnap.exists()) members = gSnap.data().members || [];
        } catch (e) {}
      }

      const unreadUpdates = {};
      if (Array.isArray(members) && members.length > 0) {
        members.forEach(mUid => {
          if (mUid !== currentUid) unreadUpdates[`unreadCount.${mUid}`] = increment(1);
        });
      }

      await updateDoc(doc(db, 'groups', actualGroupId), {
        lastMessage: `${currentUserData?.fullName || 'User'}: ${summaryText}`,
        updatedAt: serverTimestamp(),
        [`unreadCount.${currentUid}`]: 0,
        ...unreadUpdates
      }).catch(async (err) => {
        const unreadObj = {};
        const fallbackMembers = Array.isArray(members) ? members : (Array.isArray(groupData?.members) ? groupData.members : []);
        fallbackMembers.forEach(mUid => {
          if (mUid !== currentUid) unreadObj[mUid] = 1;
        });
        unreadObj[currentUid] = 0;
        await setDoc(doc(db, 'groups', actualGroupId), {
          lastMessage: `${currentUserData?.fullName || 'User'}: ${summaryText}`,
          updatedAt: serverTimestamp(),
          unreadCount: unreadObj
        }, { merge: true });
      });
      pendingMessagesRef.current.delete(clientMessageId);
      setReplyingToMsg(null);
    } catch (err) {
      pendingMessagesRef.current.delete(clientMessageId);
      setMessages(prev => prev.filter(m => m.clientMessageId !== clientMessageId));
    } finally { setUploadProgress(0); }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    const msgText = text.trim();
    
    if (msgText === '@link') {
      setShowMeetForm(true); setText('');
      if (textareaRef.current) textareaRef.current.style.height = '20px'; 
      return;
    }
    if (msgText.startsWith('@@meet_card|')) {
      const parts = msgText.split('|');
      pushMessageToFirestore('meet_card', { companyName: parts[1], meetTime: parts[2], meetLink: parts[3] }, '📅 Live Meeting');
      setText('');
      if (textareaRef.current) textareaRef.current.style.height = '20px'; 
      return;
    }

    if (msgText.startsWith('@@community_card|')) {
      const parts = msgText.split('|');
      pushMessageToFirestore('community_invite', { communityName: parts[1], inviteCommunityId: parts[2] }, '🔗 Community Invite');
      setText('');
      if (textareaRef.current) textareaRef.current.style.height = '20px'; 
      return;
    }

    if (msgText === '@@group') {
      pushMessageToFirestore('group_invite', { inviteGroupId: actualGroupId, groupName: groupData?.name || 'Group' }, '💌 Group Invite');
      setText('');
      if (textareaRef.current) textareaRef.current.style.height = '20px'; 
      return;
    }

    if (msgText.startsWith('@@group_invite|')) {
      const parts = msgText.split('|');
      pushMessageToFirestore('group_invite', { inviteGroupId: parts[1], groupName: parts[2] || 'Group' }, '💌 Group Invite');
      setText('');
      if (textareaRef.current) textareaRef.current.style.height = '20px'; 
      return;
    }

    setText('');
    setShowMentionList(false);
    if (textareaRef.current) textareaRef.current.style.height = '20px'; 

    if (editingMsg) {
      try {
        await updateDoc(doc(db, 'groups', actualGroupId, 'messages', editingMsg.id), { text: msgText, isEdited: true });
        setEditingMsg(null);
      } catch (e) {}
    } else {
      pushMessageToFirestore('text', { text: msgText }, msgText);
    }
  };

  const handleSendMeetCard = () => {
    if(!meetLinkInput) return;
    setShowMeetForm(false);
    pushMessageToFirestore('meet_card', { companyName: meetCompanyName, meetTime: meetTime, meetLink: meetLinkInput }, '📅 Live Meeting');
    setMeetLinkInput('');
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setText(val);
    if (textareaRef.current) {
      textareaRef.current.style.height = '20px';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
    const lastChar = val.slice(-1); const atIndex = val.lastIndexOf('@');
    if (atIndex !== -1 && (lastChar === '@' || !val.substring(atIndex).includes(' '))) {
      setShowMentionList(true); setMentionQuery(val.substring(atIndex + 1).toLowerCase());
    } else { setShowMentionList(false); }
  };

  const handleSelectMention = (member) => {
    const atIndex = text.lastIndexOf('@');
    const prefix = text.substring(0, atIndex);
    const finalTxt = `${prefix}@${member.displayName} `;
    setText(finalTxt); setShowMentionList(false);
    if (textareaRef.current) textareaRef.current.style.height = '20px';
  };

  // Photo & Video Upload using Firebase Storage
  const handleMediaSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setIsUploading(true);
      setUploadProgress(1);

      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_').slice(-120);
      const safeGroupId = String(actualGroupId || 'group').replace(/[^a-zA-Z0-9_-]/g, '_');
      const safeUid = String(currentUid || 'user').replace(/[^a-zA-Z0-9_-]/g, '_');

      if (file.type.startsWith('video/')) {
        const storagePath = `groupVideos/${safeGroupId}/${safeUid}/${Date.now()}_${safeName}`;
        const fileRef = storageRef(storage, storagePath);

        const uploadTask = uploadBytesResumable(fileRef, file, {
          contentType: file.type || 'video/mp4',
          cacheControl: 'public,max-age=31536000'
        });

        uploadTask.on(
          'state_changed',
          (snapshot) => {
            if (!snapshot.totalBytes) return;
            const progress = Math.floor((snapshot.bytesTransferred / snapshot.totalBytes) * 95);
            setUploadProgress(Math.min(95, progress));
          },
          (error) => {
            console.error('Group video upload failed:', error);
            setUploadProgress(0);
            setIsUploading(false);
            showToast("ভিডিও পাঠানো যায়নি।");
          },
          async () => {
            try {
              setUploadProgress(97);
              const videoURL = await getDownloadURL(uploadTask.snapshot.ref);
              setUploadProgress(98);

              await pushMessageToFirestore(
                'video',
                {
                  mediaData: videoURL,
                  mediaPath: storagePath,
                  fileName: file.name,
                  fileSize: file.size,
                  mimeType: file.type || 'video/mp4'
                },
                '🎥 Video'
              );

              setUploadProgress(100);
              setTimeout(() => setUploadProgress(0), 500);

            } catch (error) {
              console.error('Group video URL/message failed:', error);
              setUploadProgress(0);
              showToast("ভিডিও পাঠানো যায়নি।");
            } finally {
              setIsUploading(false);
            }
          }
        );

      } else {
        const base64Img = await compressImage(file);
        const blob = await (await fetch(base64Img)).blob();
        
        const storagePath = `groupImages/${safeGroupId}/${safeUid}/${Date.now()}_${safeName}`;
        const fileRef = storageRef(storage, storagePath);

        const uploadTask = uploadBytesResumable(fileRef, blob, {
          contentType: file.type || 'image/jpeg',
          cacheControl: 'public,max-age=31536000'
        });

        uploadTask.on(
          'state_changed',
          (snapshot) => {
            if (!snapshot.totalBytes) return;
            const progress = Math.floor((snapshot.bytesTransferred / snapshot.totalBytes) * 95);
            setUploadProgress(Math.min(95, progress));
          },
          (error) => {
            console.error('Group image upload failed:', error);
            setUploadProgress(0);
            setIsUploading(false);
            showToast("ছবি পাঠানো যায়নি।");
          },
          async () => {
            try {
              setUploadProgress(97);
              const imageURL = await getDownloadURL(uploadTask.snapshot.ref);
              setUploadProgress(98);

              await pushMessageToFirestore(
                'image',
                {
                  mediaData: imageURL,
                  mediaPath: storagePath,
                  fileName: file.name,
                  fileSize: file.size
                },
                '📷 Photo'
              );

              setUploadProgress(100);
              setTimeout(() => setUploadProgress(0), 500);

            } catch (error) {
              console.error('Group image URL/message failed:', error);
              setUploadProgress(0);
              showToast("ছবি পাঠানো যায়নি।");
            } finally {
              setIsUploading(false);
            }
          }
        );
      }

    } catch (error) {
      console.error('Group media upload error:', error);
      setUploadProgress(0);
      setIsUploading(false);
      showToast("মিডিয়া পাঠানো যায়নি।");
    } finally {
      if (e.target) e.target.value = '';
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setIsUploading(true);
      setUploadProgress(1);

      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_').slice(-120);
      const safeGroupId = String(actualGroupId || 'group').replace(/[^a-zA-Z0-9_-]/g, '_');
      const safeUid = String(currentUid || 'user').replace(/[^a-zA-Z0-9_-]/g, '_');

      const storagePath = `groupFiles/${safeGroupId}/${safeUid}/${Date.now()}_${safeName}`;
      const fileRef = storageRef(storage, storagePath);

      const uploadTask = uploadBytesResumable(fileRef, file, {
        contentType: file.type || 'application/octet-stream',
        cacheControl: 'public,max-age=31536000'
      });

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          if (!snapshot.totalBytes) return;
          const progress = Math.floor((snapshot.bytesTransferred / snapshot.totalBytes) * 95);
          setUploadProgress(Math.min(95, progress));
        },
        (error) => {
          console.error('Group file upload failed:', error);
          setUploadProgress(0);
          setIsUploading(false);
          showToast("ফাইল পাঠানো যায়নি।");
        },
        async () => {
          try {
            setUploadProgress(97);
            const fileURL = await getDownloadURL(uploadTask.snapshot.ref);
            setUploadProgress(98);

            await pushMessageToFirestore(
              'file',
              {
                fileData: fileURL,
                fileURL: fileURL,
                mediaPath: storagePath,
                fileName: file.name,
                fileSize: file.size,
                mimeType: file.type || 'application/octet-stream'
              },
              `📄 ${file.name}`
            );

            setUploadProgress(100);
            setTimeout(() => setUploadProgress(0), 500);

          } catch (error) {
            console.error('Group file URL/message failed:', error);
            setUploadProgress(0);
            showToast("ফাইল পাঠানো যায়নি।");
          } finally {
            setIsUploading(false);
          }
        }
      );

    } catch (error) {
      console.error('Group file upload error:', error);
      setUploadProgress(0);
      setIsUploading(false);
      showToast("ফাইল পাঠানো যায়নি।");
    } finally {
      if (e.target) e.target.value = '';
    }
  };

  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const file = items[i].getAsFile();
        if (!file) continue;
        e.preventDefault();
        setUploadProgress(10);
        const compressedImg = await compressImage(file, true); 
        setUploadProgress(40);
        const blob = await (await fetch(compressedImg)).blob();
        const newFile = new File([blob], 'pasted_sticker.png', { type: 'image/png' });
        const uploadedUrl = await uploadToImgBB(newFile);
        setUploadProgress(90);
        if (uploadedUrl) { pushMessageToFirestore('sticker', { mediaData: uploadedUrl }, '🎇 Sticker'); }
        setUploadProgress(100); setTimeout(() => setUploadProgress(0), 400);
        break;
      }
    }
  };

  const handleCreateSticker = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIsUploading(true);
    const compressedImg = await compressImage(file, true);
    const blob = await (await fetch(compressedImg)).blob();
    const newFile = new File([blob], 'sticker.png', { type: 'image/png' });
    const uploadedUrl = await uploadToImgBB(newFile);
    setIsUploading(false);
    if (uploadedUrl) {
      const newStickers = [uploadedUrl, ...savedStickers];
      setSavedStickers(newStickers);
      try {
        localStorage.setItem('nexora_saved_stickers', JSON.stringify(newStickers));
      } catch(e) {}
      showToast("Sticker created!");
    }
  };

  const handleSendSticker = (url) => { setShowStickerModal(false); pushMessageToFirestore('sticker', { mediaData: url }, '🎇 Sticker'); };
  
  const saveSomeoneSticker = (url) => {
    if(!savedStickers.includes(url)) {
      const newStickers = [url, ...savedStickers];
      setSavedStickers(newStickers); 
      try { localStorage.setItem('nexora_saved_stickers', JSON.stringify(newStickers)); } catch(e){}
      showToast("Sticker saved!");
    } else { showToast("Already saved!"); }
    setMessageActionModal(null);
  };
  
  const deleteSavedSticker = async (url) => {
    if (await window.nexoraConfirm("স্টিকারটি ডিলেট করতে চান?")) {
      const newStickers = savedStickers.filter(s => s !== url);
      setSavedStickers(newStickers); 
      try { localStorage.setItem('nexora_saved_stickers', JSON.stringify(newStickers)); } catch(e){}
    }
  };

  const handlePinMessage = async (msg) => {
    if (!isAdmin) return;
    const currentPins = groupData.pinnedMessages || [];
    if (currentPins.find(p => p.id === msg.id)) { return; }
    const newPins = [msg, ...currentPins].slice(0, 3);
    try {
      await updateDoc(doc(db, 'groups', actualGroupId), { pinnedMessages: newPins });
      setMessageActionModal(null); 
      showToast("Message pinned!");
    } catch (e) {}
  };

  const handleUnpinMessage = async (msgId) => {
    if (!isAdmin) return;
    const newPins = (groupData.pinnedMessages || []).filter(p => p.id !== msgId);
    try { 
        await updateDoc(doc(db, 'groups', actualGroupId), { pinnedMessages: newPins }); 
        setMessageActionModal(null);
        showToast("Message unpinned!");
    } catch (e) {}
  };

  const cyclePinnedMessage = (e) => {
      e.stopPropagation();
      const pins = groupData?.pinnedMessages || [];
      if(pins.length === 0) return;
      const nextIndex = (activePinIndex + 1) % pins.length;
      setActivePinIndex(nextIndex);
      const targetMsg = pins[activePinIndex];
      
      const el = document.getElementById(`msg-${targetMsg.id}`);
      if(el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setHighlightedMsgId(targetMsg.id);
          setTimeout(() => setHighlightedMsgId(null), 2400); 
      }
  };

  const handleForwardMessage = async () => {
    if (selectedForwardIds.length === 0 || !msgToForward) return;
    setIsUploading(true);
    try {
      const msgsArray = Array.isArray(msgToForward) ? msgToForward : [msgToForward];

      for (let targetId of selectedForwardIds) {
        const target = forwardTargets.find(t => t.id === targetId);
        if (!target) continue;
        const path = target.isGroup ? 'groups' : target.isCommunity ? 'communities' : 'chats';
        
        for(let m of msgsArray) {
            const msgData = {
              senderId: currentUid, senderName: currentUserData?.fullName || 'User', senderPhoto: currentUserData?.photoURL || '',
              type: m.type, createdAt: serverTimestamp(), timeStr: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true }),
              text: m.text || null, mediaData: m.mediaData || null, fileData: m.fileData || null, fileName: m.fileName || null, audioData: m.audioData || null, duration: m.duration || null, waveform: m.waveform || null,
              companyName: m.companyName || null, meetTime: m.meetTime || null, meetLink: m.meetLink || null, inviteGroupId: m.inviteGroupId || null, groupName: m.groupName || null,
              isForwarded: true
            };
            await addDoc(collection(db, path, target.id, 'messages'), msgData);
        }
        await updateDoc(doc(db, path, target.id), { lastMessage: `${currentUserData?.fullName || 'User'}: Forwarded message(s)`, updatedAt: serverTimestamp() });
      }
    
      showToast("Forwarded successfully!");
      setShowForwardModal(false); setSelectedForwardIds([]); setMsgToForward(null); setMessageActionModal(null);
      setSelectedMessageIds([]); 
    } catch (e) {} finally { setIsUploading(false); }
  };

  const getSafeAudioElement = (e) => {
    const audio = e?.currentTarget || e?.target || null;
    return audio && typeof audio === 'object' ? audio : null;
  };

  const getSafeAudioDuration = (audio) => {
    if (!audio) return 0;
    try {
      const value = Number(audio.duration);
      return Number.isFinite(value) && value >= 0 ? value : 0;
    } catch (_) {
      return 0;
    }
  };

  const getSafeAudioCurrentTime = (audio) => {
    if (!audio) return 0;
    try {
      const value = Number(audio.currentTime);
      return Number.isFinite(value) && value >= 0 ? value : 0;
    } catch (_) {
      return 0;
    }
  };

  const handleAudioPlay = (e) => {
    const current = getSafeAudioElement(e);
    if (!current) return;

    try {
      document.querySelectorAll('audio[data-nexora-voice]').forEach(a => {
        if (a !== current) {
          try { a.pause(); } catch (_) {}
        }
      });
    } catch (_) {}

    const id = typeof current.id === 'string' ? current.id.replace('voice-', '') : '';
    if (id) setPlayingVoiceId(id);
  };

  const handleVoiceLoadedMetadata = (e, id) => {
    if (!id) return;
    const audio = getSafeAudioElement(e);
    const duration = getSafeAudioDuration(audio);

    setVoicePlayback(prev => {
      const previous = prev?.[id] || {};
      return {
        ...prev,
        [id]: {
          ...previous,
          duration: duration > 0 ? duration : (Number(previous.duration) || 0),
          current: Number(previous.current) || 0
        }
      };
    });
  };

  const handleVoiceTimeUpdate = (e, id) => {
    if (!id) return;
    const audio = getSafeAudioElement(e);
    const current = getSafeAudioCurrentTime(audio);
    const duration = getSafeAudioDuration(audio);

    setVoicePlayback(prev => {
      const previous = prev?.[id] || {};
      return {
        ...prev,
        [id]: {
          ...previous,
          current,
          duration: duration > 0 ? duration : (Number(previous.duration) || 0)
        }
      };
    });
  };

  const handleVoiceEnded = (id) => {
    if (!id) return;
    setPlayingVoiceId(null);
    setVoicePlayback(prev => {
      const previous = prev?.[id] || {};
      const duration = Number(previous.duration) || 0;
      return {
        ...prev,
        [id]: {
          ...previous,
          current: duration,
          playing: false
        }
      };
    });
  };

  const toggleVoicePlayback = (id) => {
    if (!id) return;

    const audio = document.getElementById(`voice-${id}`);
    if (!audio || typeof audio.play !== 'function') return;

    try {
      document.querySelectorAll('audio[data-nexora-voice]').forEach(a => {
        if (a !== audio) {
          try { a.pause(); } catch (_) {}
        }
      });
    } catch (_) {}

    if (audio.paused) {
      Promise.resolve(audio.play())
        .then(() => setPlayingVoiceId(id))
        .catch(() => {});
    } else {
      try { audio.pause(); } catch (_) {}
      setPlayingVoiceId(null);
    }
  };

  const formatVoiceSeconds = (seconds) => {
    const s = Math.max(0, Math.floor(Number(seconds) || 0));
    return `${Math.floor(s/60)}:${String(s%60).padStart(2,'0')}`;
  };

  const startMicPreviewStream = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        return null;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = audioCtx.createAnalyser();
      const source = audioCtx.createMediaStreamSource(stream);

      analyser.fftSize = 64;
      analyser.smoothingTimeConstant = 0.55;
      source.connect(analyser);

      audioContextRef.current = audioCtx;
      analyserRef.current = analyser;

      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      const updateWave = () => {
        analyser.getByteFrequencyData(dataArray);

        const bars = Array.from(dataArray.slice(0, 10)).map((v) => {
          const noiseFloor = 2;
          if (v <= noiseFloor) return 6;
          let normalized = (v - noiseFloor) / (255 - noiseFloor);
          normalized = Math.min(1, Math.pow(normalized, 0.55) * 1.7);
          return Math.round(6 + normalized * 30);
        });

        setWaveBars(bars);
        animFrameRef.current = requestAnimationFrame(updateWave);
      };

      updateWave();
      return stream;
    } catch (e) { 
      console.warn("Mic preview stream request:", e);
      return null; 
    }
  };

  const stopMicPreviewStream = () => {
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') audioContextRef.current.close().catch(() => {});
  };

  const openVoiceModal = async () => { 
    setShowVoiceModal(true); 
    setIsRecording(false); 
    setRecordingTime(0); 
    await startMicPreviewStream(); 
  };
  
  // Voice Recording with browser permission prompt
  const handleCircleVoiceClick = async () => {
    if (isRecording) { 
      stopAndSendVoice(); 
    } else {
      try {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          showToast("Microphone not supported!");
          return;
        }

        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        mediaRecorderRef.current = new MediaRecorder(stream);
        audioChunksRef.current = [];
        mediaRecorderRef.current.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
        
        mediaRecorderRef.current.onstop = async () => {
          stopMicPreviewStream();
          setIsUploading(true);
          setUploadProgress(1);

          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          const waveform = await buildRealWaveform(audioBlob);

          const safeGroupId = String(actualGroupId || 'group').replace(/[^a-zA-Z0-9_-]/g, '_');
          const safeUid = String(currentUid || 'user').replace(/[^a-zA-Z0-9_-]/g, '_');

          const storagePath = `groupVoice/${safeGroupId}/${safeUid}/${Date.now()}.webm`;
          const fileRef = storageRef(storage, storagePath);
          const uploadTask = uploadBytesResumable(fileRef, audioBlob, { contentType: 'audio/webm' });

          uploadTask.on(
            'state_changed',
            (snapshot) => {
              if (!snapshot.totalBytes) return;
              const progress = Math.floor((snapshot.bytesTransferred / snapshot.totalBytes) * 95);
              setUploadProgress(Math.min(95, progress));
            },
            (error) => {
              console.error('Voice upload failed:', error);
              setUploadProgress(0);
              setIsUploading(false);
              showToast("ভয়েস পাঠানো যায়নি।");
            },
            async () => {
              try {
                setUploadProgress(98);
                const voiceURL = await getDownloadURL(uploadTask.snapshot.ref);

                await pushMessageToFirestore('voice', { 
                  audioData: voiceURL,
                  mediaPath: storagePath, 
                  duration: `${recordingTime}s`,
                  waveform
                }, '🎙️ Voice message');

                setUploadProgress(100);
                setTimeout(() => { setUploadProgress(0); }, 400);

              } catch (error) {
                console.error('Voice URL/message failed:', error);
                setUploadProgress(0);
                showToast("ভয়েস পাঠানো যায়নি।");
              } finally {
                setIsUploading(false);
              }
            }
          );

          stream.getTracks().forEach((t) => t.stop());
        };

        mediaRecorderRef.current.start(); 
        setIsRecording(true); 
        setRecordingTime(0);
        recordingIntervalRef.current = setInterval(() => { setRecordingTime((prev) => prev + 1); }, 1000);

      } catch (err) {
        console.error("Mic permission error:", err);
        showToast("Microphone permission denied!");
      }
    }
  };

  const stopAndSendVoice = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop(); setIsRecording(false); clearInterval(recordingIntervalRef.current); setShowVoiceModal(false);
    }
  };

  const cancelVoiceModal = () => {
    if (mediaRecorderRef.current && isRecording) { mediaRecorderRef.current.onstop = null; mediaRecorderRef.current.stop(); }
    stopMicPreviewStream(); clearInterval(recordingIntervalRef.current); setIsRecording(false); setShowVoiceModal(false);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !actualGroupId) return;
    setUploadingImage(true); const uploadedUrl = await uploadToImgBB(file); setUploadingImage(false);
    if (uploadedUrl) { await updateDoc(doc(db, 'groups', actualGroupId), { photoURL: uploadedUrl }); }
  };
  
  const handleSaveGroupInfo = async () => {
    if (!editGroupName.trim() || !actualGroupId) return;
    try { await updateDoc(doc(db, 'groups', actualGroupId), { name: editGroupName, bio: editGroupBio }); setIsEditingInfo(false); showToast("Group info updated!"); } catch (e) {}
  };
  
  const toggleMakeAdmin = async (targetMemberUid) => {
    if (!groupData || !actualGroupId) return;
    const isAlreadyAdmin = groupData.admins?.includes(targetMemberUid);
    try {
      if (isAlreadyAdmin) await updateDoc(doc(db, 'groups', actualGroupId), { admins: arrayRemove(targetMemberUid) });
      else await updateDoc(doc(db, 'groups', actualGroupId), { admins: arrayUnion(targetMemberUid) });
      showToast(isAlreadyAdmin ? "Dismissed from Admin" : "Made Admin!");
    } catch (e) {}
  };
  
  const handleRemoveMember = async (targetMemberUid) => {
    if (!actualGroupId) return;
    if (await window.nexoraConfirm("রিমুভ করতে চান?")) {
      try { 
        await updateDoc(doc(db, 'groups', actualGroupId), { members: arrayRemove(targetMemberUid), admins: arrayRemove(targetMemberUid) }); 
        const uDoc = await getDoc(doc(db, 'users', targetMemberUid));
        if(uDoc.exists()){
          await addDoc(collection(db, 'groups', actualGroupId, 'messages'), {
            type: 'system', text: `${uDoc.data().fullName || 'Member'} was removed by Admin`, createdAt: serverTimestamp(), senderId: 'system'
          });
        }
        showToast("Member removed!");
      } catch (e) {}
    }
  };
  
  const handleLeaveGroup = async () => {
    if (!actualGroupId) return;
    if (await window.nexoraConfirm("আপনি কি গ্রুপ থেকে লিভ নিতে চান?")) {
      try { 
        await updateDoc(doc(db, 'groups', actualGroupId), { members: arrayRemove(currentUid), admins: arrayRemove(currentUid) }); 
        await addDoc(collection(db, 'groups', actualGroupId, 'messages'), {
          type: 'system', text: `${currentUserData?.fullName || 'Member'} left the group`, createdAt: serverTimestamp(), senderId: 'system'
        });
        navigate('/dashboard'); 
      } catch (e) {}
    }
  };

  const toggleOnlyAdminsMessage = async () => {
    if (!isAdmin || !actualGroupId) return;
    const currentVal = !!groupData?.onlyAdminsMessage;
    try {
      await updateDoc(doc(db, 'groups', actualGroupId), { onlyAdminsMessage: !currentVal });
      showToast(currentVal ? "All members can message now" : "Only admins can message");
    } catch (e) {}
  };

  const renderTicks = (msg) => {
    if (msg.senderId !== currentUid || msg.type === 'system') return null;
    const seenUids = Object.keys(msg.seenBy || {}).filter(uid => uid !== currentUid);
    const totalOtherMembers = (groupData?.members?.length || 1) - 1;

    if (seenUids.length > 0) {
      if (seenUids.length >= totalOtherMembers && totalOtherMembers > 0) {
         return (
           <svg width="14" height="14" viewBox="0 0 24 24" fill="#22c55e" style={{ marginLeft: '4px', flexShrink: 0 }}>
             <circle cx="12" cy="12" r="10"></circle><path d="M8 12.5l3 3 5-6" stroke="#fff" strokeWidth="2.5" fill="none" strokeLinecap="round"></path>
           </svg>
         );
      } else {
         return (
           <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" style={{ marginLeft: '4px', flexShrink: 0 }}>
             <polyline points="18 6 7 17 2 12"></polyline><polyline points="22 6 11 17 9.5 15.5"></polyline>
           </svg>
         );
      }
    }
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: '4px', flexShrink: 0 }}>
        <polyline points="20 6 9 17 4 12"></polyline>
      </svg>
    );
  };

  const allMentionsList = [{ uid: 'all', displayName: 'All Mention' }, ...groupMembersList];
  const filteredMentionMembers = allMentionsList.filter(m => m.displayName.toLowerCase().includes(mentionQuery));

  return (
    <div style={styles.container}>
      <style>{`
        body, html, #root { margin: 0; padding: 0; height: 100dvh; width: 100vw; overflow: hidden; background: #fafafa; }
        * { box-sizing: border-box; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; -webkit-tap-highlight-color: transparent; -webkit-touch-callout: none; }
        
        .group-card { width: 100%; max-width: 480px; height: 100dvh; background: #fafafa; display: flex; flex-direction: column; position: relative; overflow: hidden; box-shadow: 0 0 40px rgba(0,0,0,0.04); will-change: transform; }
        @media (min-width: 768px) { .group-card { height: 92vh; max-height: 92vh; border-radius: 36px; border: 1px solid #f1f5f9; } }
        @media (max-width: 600px) { .voice-message-card { width: 250px; } }
        
        .header-bar { background: #ffffff; padding: 12px 16px; padding-top: max(16px, env(safe-area-inset-top)); display: flex; justify-content: space-between; align-items: center; z-index: 20; border-bottom: 1px solid #f1f5f9; flex: 0 0 auto; }
        .chat-input-form { padding: 10px 12px; padding-bottom: max(12px, env(safe-area-inset-bottom)); background: #ffffff; display: flex; align-items: center; gap: 8px; z-index: 20; border-top: 1px solid #f1f5f9; flex: 0 0 auto; }
        
        .scroll-area { flex: 1 1 auto; overflow-y: auto; padding: 12px 0; -webkit-overflow-scrolling: touch; }
        .chat-messages-area { padding: 12px; display: flex; flex-direction: column; gap: 6px; }

        .msg-bubble-me { background: #62b875; color: #ffffff; border-radius: 18px 18px 2px 18px; padding: 8px 12px; max-width: 80%; align-self: flex-end; font-size: 14.5px; word-break: break-word; position: relative; user-select: none; -webkit-user-select: none; transition: transform 0.2s cubic-bezier(0.25, 1, 0.5, 1); }
        .msg-bubble-other { background: #ffffff; color: #0f172a; border-radius: 18px 18px 18px 2px; padding: 8px 12px; max-width: 80%; align-self: flex-start; font-size: 14.5px; word-break: break-word; border: 1px solid #f1f5f9; position: relative; user-select: none; -webkit-user-select: none; transition: transform 0.2s cubic-bezier(0.25, 1, 0.5, 1); }
        .msg-bubble-voice-me { align-self: flex-end; position: relative; user-select: none; -webkit-user-select: none; margin-bottom: 6px; transition: transform 0.2s cubic-bezier(0.25, 1, 0.5, 1); }
        .msg-bubble-voice-other { align-self: flex-start; position: relative; user-select: none; -webkit-user-select: none; margin-bottom: 6px; transition: transform 0.2s cubic-bezier(0.25, 1, 0.5, 1); }

        .voice-message-card {
          width: 260px;
          min-height: 76px;
          display: flex;
          align-items: center;
          gap: 9px;
          padding: 8px 10px;
          border-radius: 18px;
        }
        .voice-message-card.voice-me { background: #62b875; color: #fff; }
        .voice-message-card.voice-other { background: #fff; color: #16a34a; border: 1px solid #e2e8f0; }
        .voice-play-btn {
          width: 38px;
          height: 38px;
          border: 0;
          border-radius: 50%;
          flex: 0 0 38px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 15px;
          cursor: pointer;
        }
        .voice-me .voice-play-btn { background: #fff; color: #62b875; }
        .voice-other .voice-play-btn { background: #22c55e; color: #fff; }
        .voice-wave-wrap { flex: 1; min-width: 0; }
        .voice-wave { height: 42px; display: flex; align-items: center; gap: 2px; overflow: hidden; }
        .voice-wave span { width: 3px; min-height: 5px; border-radius: 3px; transition: background .12s linear; }
        .voice-meta { display: flex; justify-content: space-between; font-size: 10px; opacity: .86; margin-top: 2px; }
        .voice-avatar { width: 34px; height: 34px; border-radius: 50%; object-fit: cover; flex: 0 0 34px; }

        .msg-bubble-sticker { background: transparent; border: none; padding: 0; align-self: flex-start; transition: transform 0.2s cubic-bezier(0.25, 1, 0.5, 1); }
        .msg-bubble-sticker.is-me { align-self: flex-end; }

        @keyframes blinkGray {
          0%, 100% { background-color: transparent; }
          50% { background-color: rgba(148, 163, 184, 0.35); }
        }
        .msg-blink-container { animation: blinkGray 0.8s ease-in-out 3; border-radius: 12px; }

        .black-action-btn { width: 48px; height: 48px; border-radius: 50%; background: rgba(24, 24, 27, 0.8); color: white; border: none; display: flex; align-items: center; justify-content: center; cursor: pointer; flex-shrink: 0; }
        .fab-btn-create { position: absolute; bottom: 30px; right: 20px; width: 60px; height: 60px; border-radius: 50%; background: #62b875; color: white; border: none; display: flex; align-items: center; justify-content: center; box-shadow: 0 10px 25px rgba(98, 184, 117, 0.4); cursor: pointer; z-index: 25; }

        .contact-row { display: flex; align-items: center; justify-content: space-between; padding: 14px 20px; cursor: pointer; border-bottom: 1px solid #f8fafc; }
        .custom-checkbox { width: 22px; height: 22px; border-radius: 50%; border: 2px solid #cbd5e1; display: flex; align-items: center; justify-content: center; transition: all 0.2s; }
        .custom-checkbox.checked { background: #62b875; border-color: #62b875; }

        .mention-popup-card { position: absolute; bottom: 70px; left: 16px; right: 16px; background: #ffffff; border-radius: 16px; box-shadow: 0 10px 30px rgba(0,0,0,0.15); max-height: 200px; overflow-y: auto; z-index: 50; border: 1px solid #e2e8f0; }
        .modal-overlay { position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.6); backdrop-filter: blur(4px); z-index: 200; display: flex; align-items: flex-end; justify-content: center; padding: 20px;}
        .card-modal { width: 100%; max-width: 480px; background: white; border-radius: 28px; padding: 24px; box-shadow: 0 20px 40px rgba(0,0,0,0.15); max-height: 85vh; overflow-y: auto; margin-bottom: 0; }
        .full-screen-modal { position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: #ffffff; z-index: 300; display: flex; flex-direction: column; animation: slideInScreen 0.25s forwards; }

        .reaction-global-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; z-index: 28; }
        .floating-reaction-popup { position: fixed; background: #ffffff; padding: 4px 8px; border-radius: 30px; box-shadow: 0 10px 25px rgba(0,0,0,0.18); display: flex; gap: 4px; z-index: 30; border: 1px solid #f1f5f9; animation: popUp 0.18s ease-out; width: max-content; max-width: 90vw; overflow-x: auto; transform: translateX(-50%); }
        @keyframes popUp { from { transform: translateX(-50%) scale(0.6); opacity: 0; } to { transform: translateX(-50%) scale(1); opacity: 1; } }
        .reaction-pop-emoji { background: none; border: none; font-size: 20px; cursor: pointer; transition: transform 0.15s ease; padding: 2px 4px; }
        .reaction-pop-emoji:active { transform: scale(1.3); }

        .voice-card-bottom { width: 100%; max-width: 480px; background: #ffffff; border-radius: 28px 28px 0 0; padding: 20px 24px 28px; box-shadow: 0 -10px 30px rgba(0,0,0,0.12); position: absolute; bottom: 0; text-align: center; border-top: 1px solid #f1f5f9; animation: slideUp 0.25s ease-out; }
        @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        .voice-circle { width: 96px; height: 96px; border-radius: 50%; background: #ffffff; border: 2px solid #62b875; display: flex; align-items: center; justify-content: center; margin: 12px auto 0; cursor: pointer; transition: all 0.2s; box-shadow: 0 8px 20px rgba(98, 184, 117, 0.1); }
        .voice-circle.recording { border-color: #ef4444; box-shadow: 0 8px 20px rgba(239, 68, 68, 0.2); animation: pulseRed 1.5s infinite; }
        @keyframes pulseRed { 0% { transform: scale(0.96); } 50% { transform: scale(1.04); } 100% { transform: scale(0.96); } }

        .img-3x4 { width: 100%; max-width: 250px; aspect-ratio: 3/4; object-fit: cover; border-radius: 12px; cursor: pointer; pointer-events: auto; }
        .action-item-btn { display: flex; align-items: center; gap: 12px; padding: 14px 20px; fontSize: 15px; font-weight: 700; border-bottom: 1px solid #f1f5f9; cursor: pointer; color: #0f172a; }
        .action-item-btn:active { background: #f8fafc; }

        .anim-coin-flip { animation: coinFlip 4s ease-in-out infinite; transform-style: preserve-3d; }
        @keyframes coinFlip {
            0%, 75% { transform: rotateY(0deg) scale(1); opacity: 1; }
            85%, 90% { transform: rotateY(180deg) scale(0.5); opacity: 0; }
            100% { transform: rotateY(360deg) scale(1); opacity: 1; }
        }
        .dynamic-shield { width: 16px; height: 16px; flex-shrink: 0; }
        .shield-color { animation: colorCycle 12s ease-in-out infinite alternate; }
        @keyframes colorCycle { 0%, 10% { fill: #22c55e; } 25%, 35% { fill: #eab308; } 50%, 60% { fill: #f97316; } 75%, 85% { fill: #ef4444; } 100% { fill: #3b82f6; } }

        .gold-name {
            font-size: 14px; font-weight: 900; background: linear-gradient(110deg, #B38728 0%, #B38728 40%, #FBF5B7 48%, #ffffff 50%, #FBF5B7 52%, #B38728 60%, #B38728 100%);
            background-size: 250% auto; color: transparent; -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; animation: textShine 2.5s linear infinite; letter-spacing: 0.5px;
        }
        @keyframes textShine { 0% { background-position: 200% center; } 100% { background-position: -100% center; } }
        .v-badge-3d {
            display: inline-flex; align-items: center; justify-content: center; width: 16px; height: 16px; background: linear-gradient(135deg, #FFDF00, #D4AF37);
            border-radius: 4px; color: #fff; text-shadow: 0px 1px 2px rgba(0,0,0,0.4); font-weight: 900; font-size: 11px;
            box-shadow: inset 1px 1px 2px rgba(255,255,255,0.9), inset -1px -1px 3px rgba(0,0,0,0.3), 0 2px 4px rgba(0,0,0,0.3); border: 1px solid #c9a42e; flex-shrink: 0; margin-left: 4px;
        }
        .blinking-owner {
            font-size: 11px; font-weight: 800; color: #d4af37; text-transform: uppercase; letter-spacing: 0.5px; text-shadow: 0 1px 2px rgba(0,0,0,0.15); animation: blinkGold 1.2s ease-in-out infinite; margin-left: 2px;
        }
        @keyframes blinkGold { 50% { opacity: 0.3; } }

        .pinned-msg-bar { background: #f0fdf4; border-bottom: 1px solid #bbf7d0; padding: 8px 16px; display: flex; align-items: center; justify-content: space-between; font-size: 13px; cursor: pointer; }
        
        .mention-badge-pulse { position: absolute; top: -6px; right: -6px; background: #ef4444; color: white; width: 22px; height: 22px; border-radius: 50%; font-size: 12px; font-weight: 800; display: flex; align-items: center; justify-content: center; animation: pulseBadge 1.5s infinite; cursor: pointer; }
        @keyframes pulseBadge { 0% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7); } 70% { box-shadow: 0 0 0 8px rgba(239, 68, 68, 0); } 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); } }

        .multi-select-bar {
            position: absolute; top: 0; left: 0; width: 100%; padding: 12px 16px; padding-top: max(16px, env(safe-area-inset-top));
            background: #1e293b; color: white; display: flex; justify-content: space-between; align-items: center; z-index: 100; animation: slideDown 0.2s ease;
        }
        @keyframes slideDown { from { transform: translateY(-100%); } to { transform: translateY(0); } }

        .magic-meet-wrapper { background: #ffffff; border-radius: 16px; border: 1px solid #e2e8f0; overflow: hidden; width: 280px; box-shadow: 0 10px 25px rgba(0,0,0,0.05); text-align: center; }
        .magic-meet-header { background: #f0fdf4; padding: 16px; border-bottom: 2px dashed #bbf7d0; }
        .magic-meet-title { font-size: 20px; font-weight: 900; color: #166534; margin: 0; letter-spacing: 0.5px; }
        .magic-meet-subtitle { font-size: 11px; color: #16a34a; font-weight: 700; background: #ffffff; padding: 4px 10px; border-radius: 20px; display: inline-block; margin-top: 8px; border: 1px solid #bbf7d0; }
        .magic-meet-timebox { display: flex; justify-around: space-around; padding: 12px; background: #ffffff; border-bottom: 1px solid #f1f5f9; }
        .magic-meet-timebox div { display: flex; flexDirection: column; align-items: center; font-size: 11px; color: #64748b; }
        .magic-meet-timebox b { font-size: 14px; color: #0f172a; margin-top: 2px; }
        .magic-meet-steps { padding: 12px 16px; text-align: left; font-size: 12px; color: #334155; font-weight: 600; background: #fafafa; }
        .magic-step-item { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
        .magic-step-no { background: #16a34a; color: white; border-radius: 50%; width: 18px; height: 18px; display: flex; align-items: center; justify-content: center; font-size: 10px; flex-shrink: 0; }
        .magic-join-btn { width: calc(100% - 24px); margin: 12px; padding: 12px; background: #16a34a; color: white; border: none; border-radius: 12px; font-weight: 900; font-size: 14px; cursor: pointer; box-shadow: 0 4px 15px rgba(22, 163, 74, 0.4); }
        
        .custom-toast { position: absolute; bottom: 90px; left: 50%; transform: translateX(-50%); background: rgba(15, 23, 42, 0.9); color: white; padding: 10px 20px; border-radius: 20px; font-size: 13px; font-weight: 600; z-index: 1000; animation: fadeInOut 2s ease forwards; pointer-events: none; box-shadow: 0 4px 15px rgba(0,0,0,0.2); white-space: nowrap; }
        @keyframes fadeInOut { 0% { opacity: 0; transform: translate(-50%, 15px); } 15% { opacity: 1; transform: translate(-50%, 0); } 85% { opacity: 1; transform: translate(-50%, 0); } 100% { opacity: 0; transform: translate(-50%, -15px); } }
      `}</style>

      {/* Custom Toast Notification */}
      {toastMsg && <div className="custom-toast">{toastMsg}</div>}

      {/* Hidden Inputs */}
      <input type="file" ref={mediaInputRef} accept="image/*,video/*" onChange={handleMediaSelect} style={{ display: 'none' }} />
      <input type="file" ref={fileInputRef} accept="*" onChange={handleFileSelect} style={{ display: 'none' }} />
      <input type="file" ref={stickerImportRef} accept="image/*" onChange={handleCreateSticker} style={{ display: 'none' }} />

      {/* Magic Meet Form Modal */}
      {showMeetForm && (
        <div className="modal-overlay" onClick={() => setShowMeetForm(false)}>
          <div className="card-modal" onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px', fontSize: '18px', fontWeight: '800', color: '#166534', textAlign: 'center' }}>📅 Create Magic Meet Card</h3>
            <input type="text" placeholder="কোম্পানির নাম (e.g., Selfgars)" value={meetCompanyName} onChange={(e)=>setMeetCompanyName(e.target.value)} style={{ width: '100%', padding: '12px', border: '1px solid #cbd5e1', borderRadius: '12px', marginBottom: '10px', outline: 'none' }} />
            <input type="text" placeholder="মিটিং সময় (e.g., ২:৩০)" value={meetTime} onChange={(e)=>setMeetTime(e.target.value)} style={{ width: '100%', padding: '12px', border: '1px solid #cbd5e1', borderRadius: '12px', marginBottom: '10px', outline: 'none' }} />
            <input type="url" placeholder="Google Meet Link (https://meet...)" value={meetLinkInput} onChange={(e)=>setMeetLinkInput(e.target.value)} style={{ width: '100%', padding: '12px', border: '1px solid #cbd5e1', borderRadius: '12px', marginBottom: '16px', outline: 'none' }} />
            <button onClick={handleSendMeetCard} style={{ width: '100%', padding: '14px', background: '#16a34a', color: 'white', border: 'none', borderRadius: '12px', fontWeight: '800', cursor: 'pointer' }}>Generate & Send</button>
          </div>
        </div>
      )}

      {/* Mention Modal */}
      {showMentionModal && (
        <div className="modal-overlay" onClick={() => setShowMentionModal(false)}>
          <div className="card-modal" onClick={e => e.stopPropagation()}>
             <h3 style={{ margin: '0 0 16px', fontSize: '18px', fontWeight: '800' }}>Unread Mentions</h3>
             {unreadMentions.map((msg, i) => (
                <div key={i} onClick={() => { setShowMentionModal(false); document.getElementById(`msg-${msg.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }); setHighlightedMsgId(msg.id); setTimeout(() => setHighlightedMsgId(null), 2400); }} style={{ padding: '12px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', background: '#f8fafc', borderRadius: '12px', marginBottom: '8px' }}>
                   <div style={{ fontWeight: '700', fontSize: '13px', color: '#16a34a' }}>{msg.senderName} mentioned you:</div>
                   <div style={{ fontSize: '14px', color: '#0f172a', marginTop: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{msg.text}</div>
                </div>
             ))}
             <button onClick={() => { setUnreadMentions([]); setShowMentionModal(false); }} style={{ width: '100%', padding: '12px', background: '#f1f5f9', color: '#0f172a', borderRadius: '12px', fontWeight: '800', marginTop: '10px', border: 'none', cursor: 'pointer' }}>Clear All</button>
          </div>
        </div>
      )}

      {/* Full Screen Media Viewer */}
      {fullScreenMedia && (
        <div style={{ position: 'absolute', top:0, left:0, right:0, bottom:0, background: 'rgba(0,0,0,0.95)', zIndex: 400, display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <button onClick={() => setFullScreenMedia(null)} style={{ background: 'none', border: 'none', color: 'white', fontSize: '24px', cursor: 'pointer' }}>✕</button>
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
              <button onClick={() => { setMsgToForward(fullScreenMedia); setShowForwardModal(true); setFullScreenMedia(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2"><polyline points="15 14 20 9 15 4"/><path d="M4 20v-7a4 4 0 0 1 4-4h12"/></svg>
              </button>
              <button onClick={() => handleSaveImage(fullScreenMedia.mediaData)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
              </button>
              {(fullScreenMedia.senderId === currentUid || isAdmin) && (
                <button onClick={() => handleDeleteMessage(fullScreenMedia.id)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                </button>
              )}
            </div>
          </div>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px' }}>
            {fullScreenMedia.type === 'video' ? (
              <video src={fullScreenMedia.mediaData} controls autoPlay style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            ) : (
              <img src={fullScreenMedia.mediaData} alt="Full" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            )}
          </div>
        </div>
      )}

      {/* Action Modal (Triple Click) */}
      {messageActionModal && (
        <div className="modal-overlay" onClick={() => setMessageActionModal(null)}>
          <div className="card-modal" onClick={e => e.stopPropagation()} style={{ padding: '12px 0' }}>
            
            <div className="action-item-btn" onClick={() => { setSelectedMessageIds([messageActionModal.id]); setMessageActionModal(null); }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
              <span>Select Message</span>
            </div>

            {messageActionModal.senderId === currentUid && (
              <div className="action-item-btn" onClick={() => { setMsgDetails(messageActionModal); setMessageActionModal(null); }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                <span>Message Details</span>
              </div>
            )}
            {messageActionModal.text && (
              <div className="action-item-btn" onClick={() => { navigator.clipboard.writeText(messageActionModal.text); setMessageActionModal(null); showToast("Copied!"); }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                <span>Copy Text</span>
              </div>
            )}
            {messageActionModal.type === 'meet_card' && (
              <div className="action-item-btn" onClick={() => { navigator.clipboard.writeText(`@@meet_card|${messageActionModal.companyName}|${messageActionModal.meetTime}|${messageActionModal.meetLink}`); setMessageActionModal(null); showToast("Meeting Card Copied!"); }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                <span>Copy Meeting Card</span>
              </div>
            )}
            {messageActionModal.type === 'group_invite' && (
              <div className="action-item-btn" onClick={() => { navigator.clipboard.writeText(`@@group_invite|${messageActionModal.inviteGroupId}|${messageActionModal.groupName}`); setMessageActionModal(null); showToast("Group Invite Copied!"); }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                <span>Copy Group Invite</span>
              </div>
            )}
            {messageActionModal.type === 'sticker' && (
              <div className="action-item-btn" onClick={() => saveSomeoneSticker(messageActionModal.mediaData)}>
                <span style={{ fontSize: '20px' }}>⭐</span>
                <span>Save Sticker</span>
              </div>
            )}

            <div className="action-item-btn" onClick={() => { setMsgToForward(messageActionModal); setShowForwardModal(true); setMessageActionModal(null); }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 14 20 9 15 4"/><path d="M4 20v-7a4 4 0 0 1 4-4h12"/></svg>
              <span>Forward</span>
            </div>

            {isAdmin && (
              groupData?.pinnedMessages?.find(p => p.id === messageActionModal.id) ? (
                 <div className="action-item-btn" onClick={() => handleUnpinMessage(messageActionModal.id)}>
                   <svg width="20" height="20" viewBox="0 0 24 24" fill="#ef4444" stroke="#ef4444" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                   <span style={{color: '#ef4444'}}>Unpin Message</span>
                 </div>
              ) : (
                 <div className="action-item-btn" onClick={() => handlePinMessage(messageActionModal)}>
                   <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                   <span>Pin Message</span>
                 </div>
              )
            )}

            {(messageActionModal.senderId === currentUid || isAdmin) && (
              <div className="action-item-btn" style={{ color: '#ef4444', borderBottom: 'none' }} onClick={() => { handleDeleteMessage(messageActionModal.id); setMessageActionModal(null); }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                <span>Delete</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Forward Modal */}
      {showForwardModal && (
        <div className="modal-overlay" onClick={() => { setShowForwardModal(false); setSelectedForwardIds([]); setMsgToForward(null); }}>
          <div className="card-modal" onClick={e => e.stopPropagation()} style={{ padding: '16px', background: '#f8fafc', height: '70vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <span style={{ fontSize: '16px', fontWeight: '800', color: '#0f172a' }}>Forward to...</span>
              <button onClick={() => { setShowForwardModal(false); setSelectedForwardIds([]); setMsgToForward(null); }} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer' }}>✕</button>
            </div>
            
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {forwardTargets.map(t => {
                const isSelected = selectedForwardIds.includes(t.id);
                
                let finalName = t.title || t.name || t.fullName || t.displayName || t.chatName;
                let finalPhoto = t.photoURL || t.groupImage || t.photo || t.avatar;

                if (!t.isGroup && !t.isCommunity) {
                    const pList = t.participants || t.users || [];
                    let targetUid = t.otherUserId || t.uid || t.participantId || t.userId;
                    
                    if (!targetUid && pList.length > 0) {
                        targetUid = pList.find(p => p !== currentUid);
                    }
                    if (!targetUid && t.id && typeof t.id === 'string' && t.id.includes('_')) {
                        targetUid = t.id.split('_').find(p => p !== currentUid);
                    }
                    if (!targetUid) { targetUid = t.id; }

                    const matchedProfile = userProfilesMap[targetUid];
                    if (matchedProfile) {
                        finalName = matchedProfile.fullName || matchedProfile.name || finalName;
                        finalPhoto = matchedProfile.photoURL || finalPhoto;
                        if (matchedProfile.phoneNumber) {
                            const normP = normalizePhone(matchedProfile.phoneNumber);
                            if (localContacts[normP]) { finalName = localContacts[normP]; }
                        }
                    } else if (targetUid) {
                        const normP = normalizePhone(targetUid);
                        if (localContacts[normP]) { finalName = localContacts[normP]; }
                    }
                    finalName = finalName || 'User';
                } else { finalName = finalName || 'Group/Community'; }

                finalPhoto = finalPhoto || "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=150";

                return (
                  <div key={t.id} onClick={() => {
                    if(isSelected) setSelectedForwardIds(selectedForwardIds.filter(id => id !== t.id));
                    else setSelectedForwardIds([...selectedForwardIds, t.id]);
                  }} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px', borderBottom: '1px solid #e2e8f0', cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <img src={finalPhoto} alt="Target" style={{ width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover' }} />
                      <div>
                        <div style={{ fontWeight: '700', fontSize: '14px' }}>{finalName}</div>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>{t.isGroup ? 'Group' : t.isCommunity ? 'Community' : 'Chat'}</div>
                      </div>
                    </div>
                    <div className={`custom-checkbox ${isSelected ? 'checked' : ''}`}>{isSelected && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}</div>
                  </div>
                );
              })}
            </div>
            {selectedForwardIds.length > 0 && (
              <button onClick={handleForwardMessage} disabled={isUploading} style={{ width: '100%', padding: '14px', background: '#62b875', color: 'white', border: 'none', borderRadius: '12px', fontWeight: '800', marginTop: '10px' }}>
                {isUploading ? 'Forwarding...' : `Forward to ${selectedForwardIds.length} chats`}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Message Details Modal */}
      {msgDetails && (
        <div className="modal-overlay" onClick={() => setMsgDetails(null)}>
          <div className="card-modal" onClick={e => e.stopPropagation()} style={{ maxHeight: '80vh', display: 'flex', flexDirection: 'column', padding: '20px' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '18px', fontWeight: '800' }}>Message Details</h3>
            
            <div style={{ flex: 1, overflowY: 'auto' }}>
              <div style={{ fontSize: '13px', color: '#334155', lineHeight: '1.6', marginBottom: '16px' }}>
                <p><strong>Sent by:</strong> {msgDetails.senderName}</p>
                <p><strong>Time:</strong> {msgDetails.timeStr || new Date(msgDetails.createdAt?.seconds * 1000).toLocaleString()}</p>
              </div>

              <div style={{ fontSize: '13px', fontWeight: '800', color: '#15803d', marginBottom: '8px' }}>Seen By:</div>
              {groupMembersList.map(m => {
                const seenData = msgDetails.seenBy?.[m.uid];
                if (!seenData) return null;
                const timeVal = seenData.time;
                const dateObj = typeof timeVal === 'number' ? new Date(timeVal) : (timeVal?.toDate ? timeVal.toDate() : new Date());
                const timeStr = dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });

                return (
                  <div key={m.uid} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f8fafc', fontSize: '13px', fontWeight: '600' }}>
                    <span>{m.displayName}</span>
                    <span style={{ color: '#64748b' }}>
                      {timeStr} {msgDetails.type === 'voice' && seenData.listened ? `(Played: ${seenData.listened})` : ''}
                    </span>
                  </div>
                );
              })}

              <div style={{ fontSize: '13px', fontWeight: '800', color: '#ef4444', marginTop: '20px', marginBottom: '8px' }}>Not Seen Yet:</div>
              {groupMembersList.filter(m => !msgDetails.seenBy?.[m.uid] && m.uid !== msgDetails.senderId).map(m => (
                <div key={m.uid} style={{ padding: '6px 0', borderBottom: '1px solid #f8fafc', fontSize: '13px', fontWeight: '600', color: '#64748b' }}>{m.displayName}</div>
              ))}
            </div>
            <button onClick={() => setMsgDetails(null)} style={{ width: '100%', padding: '12px', background: '#f1f5f9', border: 'none', borderRadius: '12px', fontWeight: '800', marginTop: '16px', cursor: 'pointer' }}>Close</button>
          </div>
        </div>
      )}

      {/* Voice Recorder Sheet */}
      {showVoiceModal && (
        <div className="modal-overlay" style={{ alignItems: 'flex-end', padding: 0 }}>
          <div className="voice-card-bottom">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '14px', fontWeight: '800', color: '#0f172a' }}>Voice Note</span>
              <button onClick={cancelVoiceModal} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#64748b', padding: '0 4px' }}>✕</button>
            </div>
            <div className={`voice-circle ${isRecording ? 'recording' : ''}`} onClick={handleCircleVoiceClick}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', height: '36px' }}>
                {waveBars.map((val, idx) => (
                  <div key={idx} style={{ width: '3px', height: `${val}px`, backgroundColor: isRecording ? '#ef4444' : '#62b875', borderRadius: '3px', transition: 'height 0.05s ease' }} />
                ))}
              </div>
            </div>
            <div style={{ marginTop: '12px' }}>
              <div style={{ fontSize: '15px', fontWeight: '800', color: isRecording ? '#ef4444' : '#0f172a' }}>
                {isRecording ? `Recording... (${recordingTime}s)` : 'Tap to start record'}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
              <button onClick={cancelVoiceModal} style={{ flex: 1, padding: '10px', background: '#f1f5f9', border: 'none', borderRadius: '12px', fontWeight: '700', color: '#64748b' }}>Cancel</button>
              <button onClick={() => { cancelVoiceModal(); setTimeout(openVoiceModal, 200); }} style={{ flex: 1, padding: '10px', background: '#e2f4e6', border: 'none', borderRadius: '12px', fontWeight: '700', color: '#15803d' }}>Re-record</button>
            </div>
          </div>
        </div>
      )}

      {/* Sticker Bottom Sheet */}
      {showStickerModal && (
        <div className="modal-overlay" style={{ alignItems: 'flex-end', padding: 0 }}>
          <div className="voice-card-bottom" style={{ height: '350px', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexShrink: 0 }}>
              <span style={{ fontSize: '16px', fontWeight: '800', color: '#0f172a' }}>Stickers</span>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button onClick={() => stickerImportRef.current?.click()} style={{ background: '#16a34a', color: '#ffffff', border: 'none', padding: '6px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: '800', cursor: 'pointer', boxShadow: '0 4px 10px rgba(22, 163, 74, 0.3)' }}>
                  {isUploading ? 'Creating...' : '➕ Create Sticker'}
                </button>
                <button onClick={() => setShowStickerModal(false)} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer' }}>✕</button>
              </div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'center', paddingBottom: '20px' }}>
              {savedStickers.length === 0 && <p style={{ color: '#64748b', fontSize: '13px' }}>No stickers saved. Click Create to make one!</p>}
              {savedStickers.map((url, i) => (
                <img 
                  key={i} 
                  src={url} 
                  onClick={() => handleSendSticker(url)} 
                  onContextMenu={(e) => { e.preventDefault(); deleteSavedSticker(url); }}
                  onTouchStart={(e) => {
                    stickerLongPressTimerRef.current = setTimeout(() => {
                      if(navigator.vibrate) navigator.vibrate(50);
                      deleteSavedSticker(url);
                    }, 600);
                  }}
                  onTouchEnd={() => clearTimeout(stickerLongPressTimerRef.current)}
                  alt="Sticker" 
                  style={{ width: '70px', height: '70px', cursor: 'pointer', border: '1px solid #f1f5f9', borderRadius: '8px', padding: '4px' }} 
                />
              ))}
            </div>
            <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '10px' }}>Tap to send. Long press to delete.</div>
          </div>
        </div>
      )}

      {/* Main Group Shell */}
      <div className="group-card">

        {creationPhase === 'select_members' && (
          <>
            <div className="header-bar">
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <button onClick={() => handleNavigate('/dashboard')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#0f172a" strokeWidth="2.5"><path d="M15 18l-6-6 6-6"/></svg>
                </button>
                <div>
                  <h3 style={{ fontSize: '18px', fontWeight: '800', margin: 0, color: '#0f172a' }}>New group</h3>
                  <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '600' }}>Add participants</span>
                </div>
              </div>
            </div>
            <div className="scroll-area">
              <div style={{ padding: '8px 20px', fontSize: '13px', fontWeight: '800', color: '#64748b' }}>Contacts on Nexora</div>
              {nexoraSavedContacts.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' }}>Your contact list has no Nexora users yet.</div>
              ) : (
                nexoraSavedContacts.map((u) => {
                  const normP = normalizePhone(u.phoneNumber);
                  const savedName = localContacts[normP] || u.fullName || normP;
                  const isChecked = selectedContacts.includes(u.uid);
                  return (
                    <div key={u.uid} className="contact-row" onClick={() => toggleContactSelection(u.uid)}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                        <img src={u.photoURL || "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=150"} alt="User" style={{ width: '44px', height: '44px', borderRadius: '50%', objectFit: 'cover' }} />
                        <div>
                          <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>{savedName}</h4>
                          <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#64748b' }}>{u.phoneNumber}</p>
                        </div>
                      </div>
                      <div className={`custom-checkbox ${isChecked ? 'checked' : ''}`}>{isChecked && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}</div>
                    </div>
                  );
                })
              )}
            </div>
            {selectedContacts.length > 0 && (
              <button className="fab-btn-create" onClick={handleProceedToGroupInfo}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
              </button>
            )}
          </>
        )}

        {creationPhase === 'group_info' && (
          <>
            <div className="header-bar">
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <button onClick={() => setCreationPhase('select_members')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#0f172a" strokeWidth="2.5"><path d="M15 18l-6-6 6-6"/></svg>
                </button>
                <h3 style={{ fontSize: '18px', fontWeight: '800', margin: 0, color: '#0f172a' }}>New group</h3>
              </div>
            </div>
            <div className="scroll-area" style={{ padding: '30px 20px', textAlign: 'center' }}>
              <input type="file" ref={newGroupLogoRef} accept="image/*" onChange={handleNewGroupLogoUpload} style={{ display: 'none' }} />
              <div onClick={() => newGroupLogoRef.current.click()} style={{ width: '80px', height: '80px', borderRadius: '50%', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', border: '2px dashed #cbd5e1', cursor: 'pointer', overflow: 'hidden' }}>
                {uploadingImage ? <span style={{ fontSize: '12px', fontWeight: '700', color: '#64748b' }}>Uploading..</span> : newGroupPhotoURL ? <img src={newGroupPhotoURL} alt="Group Logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: '24px' }}>📷</span>}
              </div>
              <input type="text" placeholder="Group name (optional)" value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)} style={{ width: '100%', padding: '14px', border: '2px solid #62b875', borderRadius: '12px', fontSize: '15px', fontWeight: '600', outline: 'none', textAlign: 'center' }} />
              <p style={{ fontSize: '13px', color: '#64748b', marginTop: '12px' }}>Members: {selectedContacts.length + 1}</p>
            </div>
            <button className="fab-btn-create" onClick={handleCreateGroupInFirebase} disabled={isCreatingGroup}>
              {isCreatingGroup ? <span style={{ fontSize: '12px', fontWeight: '700' }}>...</span> : <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>}
            </button>
          </>
        )}

        {creationPhase === 'chat_screen' && (
          <>
            <input type="file" ref={logoInputRef} accept="image/*" onChange={handleLogoUpload} style={{ display: 'none' }} />

            {/* Top Multi-Select Bar overlay */}
            {selectedMessageIds.length > 0 && (
                <div className="multi-select-bar">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <button onClick={() => setSelectedMessageIds([])} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        </button>
                        <span style={{ fontSize: '18px', fontWeight: '700' }}>{selectedMessageIds.length}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                        <button onClick={handleMultiCopy} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }} title="Copy">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                        </button>
                        <button onClick={handleMultiForward} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }} title="Forward">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 14 20 9 15 4"/><path d="M4 20v-7a4 4 0 0 1 4-4h12"/></svg>
                        </button>
                        <button onClick={handleMultiDelete} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }} title="Delete">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                        </button>
                    </div>
                </div>
            )}

            {showAddMemberScreen && (
              <div className="full-screen-modal">
                <div className="header-bar">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <button onClick={() => { setShowAddMemberScreen(false); setMembersToAdd([]); setMemberSearchQuery(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#0f172a" strokeWidth="2.5"><path d="M15 18l-6-6 6-6"/></svg>
                    </button>
                    <h3 style={{ fontSize: '18px', fontWeight: '800', margin: 0, color: '#0f172a' }}>Add Members</h3>
                  </div>
                </div>
                <div className="scroll-area">
                  <div style={{ padding: '12px 20px 8px' }}>
                    <input
                      type="text"
                      value={memberSearchQuery}
                      onChange={(e) => setMemberSearchQuery(e.target.value)}
                      placeholder="Search by name or phone..."
                      style={{ width: '100%', padding: '12px 14px', border: '1px solid #e2e8f0', borderRadius: '14px', outline: 'none', fontSize: '14px', fontWeight: '600' }}
                    />
                  </div>

                  <div style={{ padding: '8px 20px', fontSize: '13px', fontWeight: '800', color: '#64748b' }}>Contacts on Nexora</div>

                  {connectionMembersLoading ? (
                    <div style={{ padding: '18px 20px', textAlign: 'center', color: '#64748b', fontSize: '13px' }}>Loading contacts...</div>
                  ) : (() => {
                    const availableToAdd = nexoraSavedContacts.filter(u => !groupData?.members?.includes(u.uid));
                    const filtered = filteredAddableMembers.filter(u => !groupData?.members?.includes(u.uid));

                    if (filtered.length === 0) {
                      return <div style={{ padding: '18px 20px', textAlign: 'center', color: '#64748b', fontSize: '13px' }}>{memberSearchQuery ? 'No contacts found' : (availableToAdd.length === 0 ? 'No contacts available' : 'No contacts found')}</div>;
                    }

                    return filtered.map((u) => {
                      const isChecked = membersToAdd.includes(u.uid);
                      return (
                        <div key={u.uid} className="contact-row" onClick={() => toggleAddMemberSelection(u.uid)}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                            <img src={u.photoURL || "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=150"} alt="User" style={{ width: '44px', height: '44px', borderRadius: '50%', objectFit: 'cover' }} />
                            <div>
                              <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>{u.displayName}</h4>
                              <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#64748b' }}>{u.phoneNumber}</p>
                            </div>
                          </div>
                          <div className={`custom-checkbox ${isChecked ? 'checked' : ''}`}>{isChecked && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}</div>
                        </div>
                      );
                    });
                  })()}
                </div>
                {membersToAdd.length > 0 && (
                  <button className="fab-btn-create" onClick={handleAddSelectedMembersToGroup} disabled={isUploading}>
                    {isUploading ? <span style={{fontSize: '12px', fontWeight:'700'}}>Adding...</span> : <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>}
                  </button>
                )}
              </div>
            )}

            {/* গ্রুপ ইনফো মডাল */}
            {showGroupInfoModal && (
              <div className="modal-overlay" onClick={() => setShowContactModal(false)}>
                <div className="card-modal" onClick={e => e.stopPropagation()}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#0f172a' }}>Group Info</h3>
                    <button onClick={() => setShowContactModal(false)} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer' }}>✕</button>
                  </div>

                  <div style={{ textAlign: 'center', position: 'relative', width: '90px', margin: '0 auto 12px' }}>
                    <img src={groupData?.photoURL || "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=200"} alt="Group" style={{ width: '90px', height: '90px', borderRadius: '50%', objectFit: 'cover', border: '3px solid #62b875' }} />
                    {isAdmin && (
                      <button onClick={() => logoInputRef.current.click()} style={{ position: 'absolute', bottom: 0, right: 0, background: '#1e293b', color: 'white', border: 'none', borderRadius: '50%', width: '30px', height: '30px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {uploadingImage ? '...' : '📷'}
                      </button>
                    )}
                  </div>

                  {isEditingInfo ? (
                    <div style={{ marginBottom: '16px' }}>
                      <input type="text" value={editGroupName} onChange={e => setEditGroupName(e.target.value)} style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '12px', fontWeight: '700', marginBottom: '8px' }} />
                      <textarea value={editGroupBio} onChange={e => setEditGroupBio(e.target.value)} placeholder="Group Bio..." style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '12px', fontSize: '13px', height: '60px' }} />
                      <button onClick={handleSaveGroupInfo} style={{ width: '100%', padding: '10px', background: '#62b875', color: 'white', border: 'none', borderRadius: '12px', fontWeight: '700', marginTop: '6px' }}>Save Changes</button>
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                      <h3 style={{ margin: '0 0 6px', fontSize: '18px', fontWeight: '800', color: '#b8860b' }}>{groupData?.name}</h3>
                      <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>{groupData?.bio || 'No bio set'}</p>
                      {isAdmin && (
                        <button onClick={() => setIsEditingInfo(true)} style={{ background: 'none', border: 'none', color: '#62b875', fontWeight: '700', fontSize: '12px', cursor: 'pointer', marginTop: '4px' }}>✏️ Edit Name/Bio</button>
                      )}
                    </div>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'center', margin: '16px 0' }}>
                    <button onClick={() => { setShowContactModal(false); setShowAddMemberScreen(true); }} style={{ width: '100%', padding: '10px', background: '#e0f2fe', color: '#0284c7', border: 'none', borderRadius: '14px', fontWeight: '800', cursor: 'pointer', fontSize: '14px' }}>
                      ➕ Add New Members
                    </button>
                  </div>
                  
                  {isAdmin && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '0 0 16px 0', padding: '12px 16px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                      <span style={{ fontSize: '14px', fontWeight: '800', color: '#0f172a' }}>Only Admins can message</span>
                      <div 
                        onClick={toggleOnlyAdminsMessage}
                        style={{ width: '44px', height: '24px', background: groupData?.onlyAdminsMessage ? '#16a34a' : '#cbd5e1', borderRadius: '12px', position: 'relative', cursor: 'pointer', transition: 'background 0.2s' }}
                      >
                        <div style={{ width: '20px', height: '20px', background: 'white', borderRadius: '50%', position: 'absolute', top: '2px', left: groupData?.onlyAdminsMessage ? '22px' : '2px', transition: 'left 0.2s', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }} />
                      </div>
                    </div>
                  )}

                  <div style={{ fontSize: '13px', fontWeight: '800', color: '#64748b', marginBottom: '8px' }}>Members ({groupMembersList.length})</div>

                  {groupMembersList.map(m => {
                    const mIsAdmin = groupData?.admins?.includes(m.uid);
                    const mUser = userProfilesMap[m.uid] || {};
                    const uRole = (mUser.role || m.role)?.toLowerCase();
                    
                    const isOwnerUser = uRole === 'owner';
                    const isAdminUser = uRole === 'admin';
                    const isPremiumUser = uRole === 'premium';
                    const isSpecialUser = isOwnerUser || isAdminUser || isPremiumUser;
                    
                    const nameColor = isAdminUser ? '#15803d' : '#0f172a';
                    
                    return (
                      <div key={m.uid} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f8fafc' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div className={isSpecialUser ? 'anim-coin-flip' : ''} style={{ width: '36px', height: '36px', perspective: '800px' }}>
                            <img src={m.photoURL || "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=150"} alt="User" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                          </div>
                          <div>
                            <span style={{ fontWeight: '700', fontSize: '14px', color: nameColor, display: 'flex', alignItems: 'center', gap: '4px' }}>
                              
                              {isOwnerUser ? <span className="gold-name">{m.displayName}</span> : m.displayName}
                              
                              {isOwnerUser && <div className="v-badge-3d">V</div>}
                              
                              {(isAdminUser || isPremiumUser) && (
                                <svg className="dynamic-shield" viewBox="0 0 100 100">
                                  <path className="shield-color" d="M50 5 L90 20 L90 50 C90 75 50 95 50 95 C50 95 10 75 10 50 L10 20 Z"/>
                                  <path d="M50 11 L84 24 L84 49 C84 70 50 87 50 87 C50 87 16 70 16 49 L16 24 Z" fill="none" stroke="white" strokeWidth="2.5" opacity="0.8"/>
                                  <path d="M32 48 L45 61 L68 38" stroke="white" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              )}
                            </span>
                            
                            {isOwnerUser ? (
                              <span className="blinking-owner">Owner</span>
                            ) : mIsAdmin ? (
                              <span style={{ fontSize: '11px', color: '#62b875', fontWeight: '800', marginLeft: '2px' }}>🛡️ Admin</span>
                            ) : null}
                          </div>
                        </div>

                        {isAdmin && m.uid !== currentUid && !isOwnerUser && (
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <button onClick={() => toggleMakeAdmin(m.uid)} style={{ padding: '4px 8px', background: '#f1f5f9', border: 'none', borderRadius: '8px', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}>{mIsAdmin ? 'Dismiss' : 'Make Admin'}</button>
                            <button onClick={() => handleRemoveMember(m.uid)} style={{ padding: '4px 8px', background: '#fef2f2', color: '#ef4444', border: 'none', borderRadius: '8px', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}>Remove</button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <button onClick={handleLeaveGroup} style={{ width: '100%', padding: '12px', background: '#fef2f2', color: '#ef4444', border: 'none', borderRadius: '16px', fontWeight: '800', marginTop: '20px', cursor: 'pointer' }}>🚪 Leave Group</button>
                  
                  {(currentUid === groupData?.adminUid || isAdmin) && (
                    <button onClick={handleDeleteGroup} style={{ width: '100%', padding: '12px', background: '#fef2f2', color: '#ef4444', border: 'none', borderRadius: '16px', fontWeight: '800', marginTop: '10px', cursor: 'pointer' }}>🚨 Delete Group</button>
                  )}
                </div>
              </div>
            )}

            {/* Header */}
            <div className="header-bar" onClick={() => setShowContactModal(true)} style={{ cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <button onClick={(e) => { e.stopPropagation(); handleNavigate('/dashboard'); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#0f172a" strokeWidth="2.5"><path d="M15 18l-6-6 6-6"/></svg>
                </button>
                <img src={groupData?.photoURL || "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=200"} alt="Group" style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }} />
                <div>
                  <h3 style={{ fontSize: '15px', fontWeight: '800', margin: 0, color: '#0f172a' }}>{groupData?.name || 'Group'}</h3>
                  <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '600' }}>{groupMembersList.length} members</span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '16px' }}>
                <button onClick={(e) => { e.stopPropagation(); handleNavigate('/call', { state: { targetUid: actualGroupId, groupId: actualGroupId, callType: 'audio', isGroupCall: true, groupMembers: groupData?.members, groupName: groupData?.name } }); }} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#0f172a" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.79 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                </button>
                <button onClick={(e) => { e.stopPropagation(); handleNavigate('/call', { state: { targetUid: actualGroupId, groupId: actualGroupId, callType: 'video', isGroupCall: true, groupMembers: groupData?.members, groupName: groupData?.name } }); }} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#0f172a" strokeWidth="2"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="3"/></svg>
                </button>
              </div>
            </div>

            {groupData?.pinnedMessages && groupData.pinnedMessages.length > 0 && (
              <div className="pinned-msg-bar" onClick={cyclePinnedMessage}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                  <span style={{ fontSize: '16px' }}>📌</span>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontWeight: '800', color: '#16a34a' }}>
                      Pinned Message {groupData.pinnedMessages.length > 1 ? `(${activePinIndex + 1}/${groupData.pinnedMessages.length})` : ''}
                    </span>
                    <span style={{ color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {groupData.pinnedMessages[activePinIndex].type === 'text' 
                          ? groupData.pinnedMessages[activePinIndex].text 
                          : `[${groupData.pinnedMessages[activePinIndex].type.toUpperCase()}]`}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {uploadProgress > 0 && (
              <div style={{ padding: '8px 16px', background: '#f0fdf4', flexShrink: 0 }}>
                <span style={{ fontSize: '12px', fontWeight: '700', color: '#62b875' }}>Sending File ({uploadProgress}%)...</span>
              </div>
            )}

            <div
              className="scroll-area chat-messages-area"
              ref={chatContainerRef}
              onScroll={handleScroll}
              style={{
                visibility: initialPositionReady ? 'visible' : 'hidden'
              }}
            >
              {showUnreadBanner && unreadBannerCount > 0 && (
                <div className="unread-top-banner">
                  {unreadBannerCount} unread messages
                </div>
              )}
              
              {reactionPopup && (
                <div className="reaction-global-overlay" onClick={() => { setReactionPopup(null); setCustomEmojiMsgId(null); }}>
                  <div 
                    className="floating-reaction-popup" 
                    style={{ top: reactionPopup.y > 100 ? reactionPopup.y - 70 : 80, left: '50%', transform: 'translateX(-50%)' }} 
                    onClick={(e) => e.stopPropagation()}
                  >
                    {['👍', '❤️', '😂', '😮', '😢', '🙏'].map(emoji => (
                      <button key={emoji} onClick={() => handleReactToPost(reactionPopup.msg.id, emoji)} className="reaction-pop-emoji">{emoji}</button>
                    ))}
                    {customEmojiMsgId === reactionPopup.msg.id ? (
                      <input autoFocus type="text" placeholder="😀" onChange={(e) => { if(e.target.value) { handleReactToPost(reactionPopup.msg.id, e.target.value); setCustomEmojiMsgId(null); } }} onBlur={() => setCustomEmojiMsgId(null)} style={{ width: '28px', fontSize: '16px', background: 'transparent', border: 'none', outline: 'none', textAlign: 'center', padding: 0 }} />
                    ) : (
                      <button onClick={(e) => { e.stopPropagation(); setCustomEmojiMsgId(reactionPopup.msg.id); }} className="reaction-pop-emoji" style={{ background: '#f1f5f9', borderRadius: '50%', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', marginLeft: '4px' }}>➕</button>
                    )}
                  </div>
                </div>
              )}

              {messages.map((msg) => {
                
                if (msg.type === 'system') {
                  return (
                    <div key={msg.id} style={{ display: 'flex', justifyContent: 'center', margin: '12px 0' }}>
                      <span style={{ background: '#f1f5f9', color: '#64748b', fontSize: '12px', padding: '4px 12px', borderRadius: '12px', fontWeight: '600' }}>
                        {msg.text}
                      </span>
                    </div>
                  );
                }

                const isMe = msg.senderId === currentUid;
                const msgTimeMillis = msg.createdAt?.seconds ? msg.createdAt.seconds * 1000 : Date.now();
                const canEdit = isMe && msg.type === 'text' && (Date.now() - msgTimeMillis < 30000);
                const isHighlighted = highlightedMsgId === msg.id;
                const isSelected = selectedMessageIds.includes(msg.id);

                let bubbleClass = isMe ? 'msg-bubble-me' : 'msg-bubble-other';
                if (msg.type === 'voice') bubbleClass = isMe ? 'msg-bubble-voice-me' : 'msg-bubble-voice-other';
                if (msg.type === 'sticker') bubbleClass = `msg-bubble-sticker ${isMe ? 'is-me' : ''}`;
                
                const senderUser = userProfilesMap[msg.senderId] || (isMe ? currentUserData : null);
                const sRole = senderUser?.role?.toLowerCase();
                
                const isOwnerMsg = sRole === 'owner';
                const isAdminMsg = sRole === 'admin';
                const isPremiumMsg = sRole === 'premium';
                const isSpecialUserMsg = isOwnerMsg || isAdminMsg || isPremiumMsg;
                const nameColor = isAdminMsg ? '#15803d' : '#0f172a'; 

                return (
                  <React.Fragment key={msg.id}>
                    {firstUnreadIdRef.current === msg.id && totalUnreadCountRef.current > 0 && (
                        <div id={`unread-divider-${msg.id}`} style={{ display: 'flex', justifyContent: 'center', margin: '16px 0' }}>
                            <div style={{ background: '#1e293b', color: '#fff', fontSize: '12px', fontWeight: '700', padding: '6px 16px', borderRadius: '16px', boxShadow: '0 4px 10px rgba(0,0,0,0.1)' }}>
                                {totalUnreadCountRef.current} unread messages
                            </div>
                        </div>
                    )}

                    <div 
                      id={`msg-${msg.id}`} 
                      className={isHighlighted ? 'msg-blink-container' : ''} 
                      onClick={() => { if(selectedMessageIds.length > 0) toggleSelection(msg.id); }}
                      style={{ 
                          display: 'flex', flexDirection: 'column', margin: '4px 0', alignItems: isMe ? 'flex-end' : 'flex-start',
                          backgroundColor: isSelected ? 'rgba(98, 184, 117, 0.15)' : 'transparent',
                          padding: isSelected ? '4px' : '0', borderRadius: '12px', transition: 'background-color 0.2s'
                      }}
                    >
                      
                      {!isMe && (
                        <div onClick={() => isAdmin && handleNavigate('/chat/' + msg.senderId)} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px', cursor: isAdmin ? 'pointer' : 'default' }}>
                          
                          <div className={isSpecialUserMsg ? 'anim-coin-flip' : ''} style={{ width: '22px', height: '22px', perspective: '800px' }}>
                            <img src={msg.senderPhoto || "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=150"} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} alt="U" />
                          </div>
                          
                          <span style={{ fontSize: '13px', color: nameColor, fontWeight: '900', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            {isOwnerMsg ? <span className="gold-name">{msg.senderName}</span> : msg.senderName}
                            {isOwnerMsg && <div className="v-badge-3d">V</div>}
                            {(isAdminMsg || isPremiumMsg) && (
                              <svg className="dynamic-shield" viewBox="0 0 100 100">
                                <path className="shield-color" d="M50 5 L90 20 L90 50 C90 75 50 95 50 95 C50 95 10 75 10 50 L10 20 Z"/>
                                <path d="M50 11 L84 24 L84 49 C84 70 50 87 50 87 C50 87 16 70 16 49 L16 24 Z" fill="none" stroke="white" strokeWidth="2.5" opacity="0.8"/>
                                <path d="M32 48 L45 61 L68 38" stroke="white" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            )}
                          </span>
                        </div>
                      )}

                      <div 
                        className={bubbleClass} 
                        style={{ transform: `translateX(${swipeX[msg.id] || 0}px)` }}
                        onClick={(e) => handleMsgClick(e, msg)} 
                        onTouchStart={(e) => handleTouchStartReact(e, msg)} 
                        onTouchMove={(e) => handleTouchMoveReact(e, msg)}
                        onTouchEnd={(e) => handleTouchEndReact(e, msg)}
                      >
                        
                        {msg.replyToMsgText && (
                          <div 
                            onClick={(e) => { e.stopPropagation(); setHighlightedMsgId(msg.replyToMsgId); document.getElementById(`msg-${msg.replyToMsgId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }); setTimeout(() => setHighlightedMsgId(null), 2400); }}
                            style={{ background: isMe ? '#16a34a' : '#f1f5f9', padding: '6px 10px', borderRadius: '8px', marginBottom: '6px', fontSize: '12px', borderLeft: '4px solid #0f172a', opacity: 0.9, cursor: 'pointer' }}
                          >
                            <strong style={{ display: 'block', marginBottom: '2px', color: isMe ? 'white' : '#0f172a' }}>{msg.replyToSenderName}</strong>
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', color: isMe ? 'white' : '#334155' }}>
                              {msg.replyToMsgText}
                            </span>
                          </div>
                        )}

                        {msg.type === 'text' && <div>{renderTextWithLinks(msg.text)}</div>}
                        
                        {msg.type === 'image' && (
                          <div style={{ padding: '3px' }}>
                            <img src={msg.mediaData} onClick={(e) => { e.stopPropagation(); setFullScreenMedia(msg); }} className="img-3x4" alt="Photo" />
                          </div>
                        )}

                        {msg.type === 'video' && (
                          <video 
                            src={msg.mediaData} 
                            controls 
                            className="img-3x4" 
                            style={{ borderRadius: '12px', outline: 'none' }} 
                          />
                        )}

                        {msg.type === 'sticker' && <img src={msg.mediaData} alt="Sticker" style={{ width: '120px', height: '120px', objectFit: 'contain' }} />}
                        
                        {msg.type === 'file' && (
                          <div>
                            <a href={msg.fileData || msg.fileURL} download={msg.fileName} style={{ color: 'inherit', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontSize: '20px' }}>📄</span>
                              <div>
                                <div style={{ fontWeight: '700', fontSize: '13px' }}>{msg.fileName}</div>
                                <div style={{ fontSize: '10px', opacity: 0.8 }}>Download File</div>
                              </div>
                            </a>
                          </div>
                        )}

                        {/* Voice Playback */}
                        {msg.type === 'voice' && (() => {
                          const vp = voicePlayback[msg.id] || {};
                          const dur = Number(vp.duration) || (parseFloat(msg.duration) || 0);
                          const cur = Number(vp.current) || 0;
                          const progressRatio = dur > 0 ? Math.min(1, cur / dur) : 0;
                          const bars = Array.isArray(msg.waveform) && msg.waveform.length
                            ? msg.waveform
                            : makeFallbackWaveform(msg.id);

                          return (
                            <div
                              className={`voice-message-card ${isMe ? 'voice-me' : 'voice-other'}`}
                              onClick={(e) => e.stopPropagation()}
                              style={{ opacity: msg.status === 'uploading' ? 0.82 : 1 }}
                            >
                              <button
                                type="button"
                                className="voice-play-btn"
                                onClick={() => toggleVoicePlayback(msg.id)}
                              >
                                {playingVoiceId === msg.id ? '❚❚' : '▶'}
                              </button>

                              <div className="voice-wave-wrap">
                                <div className="voice-wave">
                                  {bars.map((h, i) => (
                                    <span
                                      key={i}
                                      style={{
                                        height: `${h}px`,
                                        background:
                                          i / bars.length <= progressRatio
                                            ? (isMe ? '#ffffff' : '#22c55e')
                                            : (isMe ? 'rgba(255,255,255,.55)' : '#94a3b8')
                                      }}
                                    />
                                  ))}
                                </div>
                                <div className="voice-meta">
                                  <span>{formatVoiceSeconds(cur || (playingVoiceId === msg.id ? 0 : dur))}</span>
                                  <span>{msg.timeStr}</span>
                                </div>
                              </div>

                              <img
                                className="voice-avatar"
                                src={msg.senderPhoto || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=80'}
                                alt=""
                              />

                              <audio
                                id={`voice-${msg.id}`}
                                data-nexora-voice
                                src={msg.audioData}
                                preload="metadata"
                                onPlay={handleAudioPlay}
                                onPause={() => {
                                  if (playingVoiceId === msg.id) setPlayingVoiceId(null);
                                }}
                                onLoadedMetadata={(e) => handleVoiceLoadedMetadata(e, msg.id)}
                                onTimeUpdate={(e) => handleVoiceTimeUpdate(e, msg.id)}
                                onEnded={() => handleVoiceEnded(msg.id)}
                                onClick={(e) => e.stopPropagation()}
                                style={{ display: 'none' }}
                              />
                            </div>
                          );
                        })()}

                        {msg.type === 'meet_card' && (
                          <div className="magic-meet-wrapper">
                            <div className="magic-meet-header">
                              <h2 className="magic-meet-title">LIVE MEETING</h2>
                              <div className="magic-meet-subtitle">আপনার উপস্থিতি আমাদের জন্য গুরুত্বপূর্ণ</div>
                            </div>
                            <div className="magic-meet-timebox">
                              <div>আজ <b>{msg.meetTime}</b></div>
                              <div style={{ borderLeft: '1px solid #e2e8f0', paddingLeft: '12px' }}>জয়েন হওয়ার সময় <b>১০ মিনিট</b></div>
                            </div>
                            <div className="magic-meet-steps">
                              <div style={{ textAlign: 'center', marginBottom: '8px', color: '#16a34a', fontWeight: '900' }}>জয়েন করার ধাপ:</div>
                              <div className="magic-step-item"><span className="magic-step-no">01</span> Meet এ ক্লিক করুন</div>
                              <div className="magic-step-item"><span className="magic-step-no">02</span> Gmail দিয়ে সাইন ইন করুন</div>
                              <div className="magic-step-item"><span className="magic-step-no">03</span> "Ask to Join" বাটনে ক্লিক করুন</div>
                              <div className="magic-step-item"><span className="magic-step-no">04</span> মিটিংয়ে জয়েন করে স্ক্রিনশট ইবক্সে পাঠান</div>
                            </div>
                            <button onClick={(e) => { e.stopPropagation(); window.open(msg.meetLink, '_blank'); }} className="magic-join-btn">
                              JOIN MEETING
                            </button>
                          </div>
                        )}

                        {msg.type === 'group_invite' && (
                          <div className="magic-meet-wrapper">
                            <div className="magic-meet-header" style={{ background: '#fef3c7', borderBottom: '2px dashed #fde047' }}>
                              <h2 className="magic-meet-title" style={{ color: '#854d0e' }}>GROUP INVITE</h2>
                              <div className="magic-meet-subtitle" style={{ color: '#a16207', borderColor: '#fde047' }}>আপনাকে গ্রুপে আমন্ত্রণ জানানো হয়েছে</div>
                            </div>
                            <div style={{ padding: '16px', fontWeight: '900', fontSize: '18px', color: '#0f172a' }}>
                              {msg.groupName}
                            </div>
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (!msg.inviteGroupId || !currentUid) return;
                                try {
                                  const inviteGroupRef = doc(db, 'groups', msg.inviteGroupId);
                                  const inviteGroupSnap = await getDoc(inviteGroupRef);
                                  if (!inviteGroupSnap.exists()) return;

                                  const inviteGroupData = inviteGroupSnap.data();
                                  const members = Array.isArray(inviteGroupData.members) ? inviteGroupData.members : [];

                                  if (!members.includes(currentUid)) {
                                    await updateDoc(inviteGroupRef, {
                                      members: arrayUnion(currentUid)
                                    });
                                  }

                                  navigate('/group', {
                                    state: { groupId: msg.inviteGroupId },
                                    replace: true
                                  });
                                } catch (err) {}
                              }}
                              className="magic-join-btn"
                              style={{ background: '#ca8a04', boxShadow: '0 4px 15px rgba(202, 138, 4, 0.4)' }}
                            >
                              JOIN GROUP
                            </button>
                          </div>
                        )}

                        {/* Community Invite Magic Card */}
                        {msg.type === 'community_invite' && (
                          <div className="magic-meet-wrapper">
                            <div className="magic-meet-header" style={{ background: '#e0f2fe', borderBottom: '2px dashed #bae6fd' }}>
                              <h2 className="magic-meet-title" style={{ color: '#0284c7' }}>COMMUNITY INVITE</h2>
                              <div className="magic-meet-subtitle" style={{ color: '#0284c7', borderColor: '#bae6fd' }}>আপনাকে কমিউনিটিতে আমন্ত্রণ জানানো হয়েছে</div>
                            </div>
                            <div style={{ padding: '16px', fontWeight: '900', fontSize: '18px', color: '#0f172a' }}>
                              {msg.communityName}
                            </div>
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (!msg.inviteCommunityId || !currentUid) return;
                                try {
                                  const commRef = doc(db, 'communities', msg.inviteCommunityId);
                                  const commSnap = await getDoc(commRef);
                                  if (!commSnap.exists()) return;

                                  const commData = commSnap.data();
                                  const alreadyMember = commData.adminUid === currentUid || commData.admins?.includes(currentUid) || commData.members?.includes(currentUid);

                                  if (!alreadyMember) {
                                    await updateDoc(commRef, { members: arrayUnion(currentUid) });
                                  }

                                  navigate(`/community/${msg.inviteCommunityId}`, { state: { communityId: msg.inviteCommunityId } });
                                } catch (error) {
                                  console.error('Join community failed:', error);
                                }
                              }}
                              className="magic-join-btn"
                              style={{ background: '#0284c7', boxShadow: '0 4px 15px rgba(2, 132, 199, 0.4)' }}
                            >
                              JOIN COMMUNITY
                            </button>
                          </div>
                        )}

                        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
                          {canEdit && <span onClick={(e) => { e.stopPropagation(); setEditingMsg(msg); setText(msg.text); if (textareaRef.current) { textareaRef.current.style.height = 'auto'; textareaRef.current.style.height = '50px'; } }} style={{ fontSize: '13px', cursor: 'pointer' }}>✏️</span>}
                          <div style={{ fontSize: '10px', opacity: 0.8, color: msg.type === 'sticker' || msg.type === 'meet_card' || msg.type === 'group_invite' ? '#64748b' : 'inherit', display: 'flex', alignItems: 'center' }}>
                            {msg.type === 'voice' ? `${msg.duration} • ` : ''} {msg.timeStr} {msg.isEdited && '(edited)'}
                            {renderTicks(msg)}
                          </div>
                        </div>

                        {msg.reactions && Object.keys(msg.reactions).length > 0 && (
                          <div style={{ position: 'absolute', bottom: '-10px', right: '10px', background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '2px 6px', fontSize: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', display: 'flex', gap: '2px', color: '#0f172a' }}>
                            {Object.values(msg.reactions).slice(0, 3).map((r, i) => <span key={i}>{r}</span>)}
                            {Object.keys(msg.reactions).length > 3 && <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#64748b' }}>+{Object.keys(msg.reactions).length - 3}</span>}
                          </div>
                        )}
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {showScrollBottom && (
              <div 
                onClick={() => {
                   messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
                   autoScrollLockedRef.current = false;
                   setShowScrollBottom(false);
                }}
                style={{ position: 'absolute', bottom: '80px', left: '50%', transform: 'translateX(-50%)', width: '40px', height: '40px', background: '#ffffff', borderRadius: '50%', boxShadow: '0 4px 15px rgba(0,0,0,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 25 }}
              >
                <span style={{ fontSize: '18px' }}>⬇️</span>
                {unreadMentions.length > 0 && (
                   <div className="mention-badge-pulse" onClick={(e) => { e.stopPropagation(); setShowMentionModal(true); }}>
                      {unreadMentions.length}
                   </div>
                )}
              </div>
            )}

            {showMentionList && filteredMentionMembers.length > 0 && (
              <div className="mention-popup-card">
                {filteredMentionMembers.map((m, idx) => (
                  <div key={idx} onClick={() => handleSelectMention(m)} style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9' }}>
                    {m.uid === 'all' ? (
                      <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#1e293b', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 'bold' }}>@</div>
                    ) : (
                      <img src={m.photoURL || "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=150"} alt="User" style={{ width: '28px', height: '30px', borderRadius: '50%', objectFit: 'cover' }} />
                    )}
                    <span style={{ fontSize: '13px', fontWeight: '700', color: m.uid === 'all' ? '#2563eb' : '#0f172a' }}>
                      {m.uid === 'all' ? m.displayName : `@${m.displayName}`}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {canSend ? (
              <div style={{ background: '#ffffff', borderTop: '1px solid #f1f5f9', zIndex: 20, flex: '0 0 auto', display: 'flex', flexDirection: 'column' }}>
                
                {(replyingToMsg || editingMsg) && (
                  <div style={{ background: '#f8fafc', padding: '8px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0' }}>
                    <div>
                      <span style={{ fontSize: '12px', fontWeight: '800', color: '#2563eb', display: 'block' }}>
                        {editingMsg ? 'Editing message' : `Replying to ${replyingToMsg.senderName}`}
                      </span>
                      <span style={{ fontSize: '12px', color: '#64748b', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '280px' }}>
                        {editingMsg ? editingMsg.text : getReplySummaryText(replyingToMsg)}
                      </span>
                    </div>
                    <button onClick={() => { setReplyingToMsg(null); setEditingMsg(null); setText(''); if (textareaRef.current) textareaRef.current.style.height = '20px'; }} style={{ background: 'none', border: 'none', fontSize: '16px', cursor: 'pointer', color: '#64748b' }}>✕</button>
                  </div>
                )}

                <form onSubmit={handleSendMessage} className="chat-input-form" style={{ borderTop: 'none', alignItems: 'flex-end' }}>
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', background: '#f0f2f5', borderRadius: '24px', padding: '0 12px', minHeight: '40px' }}>
                    <span onClick={() => setShowStickerModal(true)} style={{ cursor: 'pointer', fontSize: '18px', marginRight: '6px' }}>😊</span>
                    
                    <textarea 
                      ref={textareaRef}
                      rows="1"
                      placeholder="Message" 
                      value={text} 
                      onChange={handleInputChange} 
                      onPaste={handlePaste}
                      style={{ flex: 1, padding: '10px 0', background: 'none', border: 'none', color: '#0f172a', outline: 'none', fontSize: '14px', resize: 'none', maxHeight: '120px', overflowY: 'auto' }} 
                    />
                    
                    <button type="button" onClick={() => fileInputRef.current?.click()} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px', paddingBottom: '2px' }} title="Attach File">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                    </button>
                    <button type="button" onClick={() => mediaInputRef.current?.click()} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px', paddingBottom: '2px' }} title="Camera">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
                    </button>
                  </div>
                  
                  {text.trim() || editingMsg ? (
                    <button type="submit" className="black-action-btn" title="Send" style={{ marginBottom: '0px' }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5" style={{ transform: 'translateY(1px)' }}><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                    </button>
                  ) : (
                    <button type="button" onClick={openVoiceModal} className="black-action-btn" title="Voice Note" style={{ marginBottom: '0px' }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
                    </button>
                  )}
                </form>
              </div>
            ) : (
              <div style={{ padding: '14px', background: '#f8fafc', textAlign: 'center', fontSize: '12px', color: '#64748b', fontWeight: '700', borderTop: '1px solid #e2e8f0', flex: '0 0 auto' }}>
                🔒 Only admins can send messages.
              </div>
            )}
          </>
        )}

      </div>
    </div>
  );
};

const styles = {
  container: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: '#fafafa', display: 'flex', justifyContent: 'center', alignItems: 'center' }
};

export default GroupScreen;